import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { useSearchParams } from "react-router-dom";
import { FaCopy, FaEdit, FaPaperclip, FaSave, FaTimes } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import ThemeProvider from "../theme/TemaMuiProvider";
import muiTheme from "../theme/muiTheme";
import { copiarTexto } from "../utils/clipboard";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import PainelAprovacao from "../components/Aprovacao/PainelAprovacao";
import DialogReprovarAprovacao from "../components/Aprovacao/DialogReprovarAprovacao";
import DialogQuestionarAprovacao from "../components/Aprovacao/DialogQuestionarAprovacao";
import DialogResponderAprovacao from "../components/Aprovacao/DialogResponderAprovacao";
import FichaCadastroPrintTemplate from "../components/FichaCadastroPrintTemplate";
import {
  CHECKLIST_FIADOR_PF,
  CHECKLIST_FIADOR_PJ,
  CHECKLIST_LOCATARIO_PF_IMOBILIARIA,
  CHECKLIST_LOCATARIO_PF_PADRAO,
  CHECKLIST_LOCATARIO_PJ_PADRAO,
  obterGruposChecklistDocumentosFicha,
  aplicarFinsGarantia,
  fichaEhParaFinsDeGarantia,
} from "../components/ficha/fichaChecklists";
import { pendenciasDaFicha } from "../components/ficha/fichaValidacao";
import { buscarDadosCnpj, fichaParaForm, fmtData, mascararCNPJ, toMoeda } from "../components/ficha/fichaFormato";
import { limparCnpj } from "../utils/documento";
import { descreverDocumentoEstrangeiro } from "../utils/documentoEstrangeiro";
import { FichaPessoaForm } from "../components/ficha/FichaPessoaForm";

// ── Constantes ────────────────────────────────────────────────────────────────

const LABEL_CAMPO = {
  Locataria: "Locatária",
  Fiador: "Fiador",
};

const PAPEIS_COM_FICHA_CADASTRAL = ["Locataria", "Fiador"];

const permiteFichaCadastral = (campoProposta) => PAPEIS_COM_FICHA_CADASTRAL.includes(campoProposta);

const EMPTY_ERROS_FORM = {};

const STATUS_GERAL_CONFIG = {
  EmDigitacao: { label: "Em Digitação", cor: "#555" },
  EmAprovacao: { label: "Em Aprovação", cor: "#1998a8" },
  Aprovada: { label: "Aprovada", cor: "#2e7d32" },
  AguardandoAssinatura: { label: "Aguard. Assinatura", cor: "#7b1fa2" },
  Concluido: { label: "Concluído", cor: "#004d40" },
  Reprovada: { label: "Reprovada", cor: "#d32f2f" },
  Inutilizada: { label: "Inutilizada", cor: "#4e342e" },
};

const STATUS_FICHAS_CONFIG = {
  EmPreenchimento: { label: "Em Preenchimento", cor: "#1998a8" },
  EmAprovacao: { label: "Em Aprovação", cor: "#1976d2" },
  Aprovada: { label: "Aprovada", cor: "#2e7d32" },
  Reprovada: { label: "Reprovada", cor: "#d32f2f" },
  Inutilizada: { label: "Inutilizada", cor: "#4e342e" },
};

// ── Filtro vindo do Dashboard (pendência de aprovação de Fichas/Análise de Crédito por usuário) ─
const LABEL_STATUS_APROVACAO = { Aprovar: "A Aprovar", Responder: "A Responder", Aguardando: "Aguardando Aprovação" };
const LABEL_PROCESSO_DASHBOARD = { Fichas: "Fichas", AnaliseCredito: "Análise de Crédito" };

const STATUS_ANALISE_CREDITO_CONFIG = {
  AguardandoUpload: { label: "Em Preenchimento", cor: "#1998a8" },
  EmAprovacao: { label: "Em Aprovação", cor: "#1976d2" },
  Aprovada: { label: "Aprovada", cor: "#2e7d32" },
  Reprovada: { label: "Reprovada", cor: "#d32f2f" },
};

// Nome do arquivo sugerido pelo navegador ao "Salvar como PDF" — usa o <title> da
// página impressa. Padrão: Código_ApelidoImóvel_PrimeiroNomeLocatária_DataInício_FC.
function primeiroNome(texto) {
  return (texto || "").trim().split(/\s+/)[0] || "";
}

function dataParaNomeArquivo(val) {
  if (!val) return "";
  const s = String(val).substring(0, 10);
  const [ano, mes, dia] = s.split("-");
  if (!ano || !mes || !dia) return "";
  return `${dia}${mes}${ano}`;
}

function nomeArquivoImpressao(proposta, sufixo) {
  const codigo = proposta?.codigo || "";
  const apelido = proposta?.imovel?.apelido || "";
  const locataria = (proposta?.fichaPessoas || []).find((f) => f.campoProposta === "Locataria");
  const nomeLocataria = primeiroNome(locataria?.razaoSocial).toUpperCase();
  const dataInicio = dataParaNomeArquivo(proposta?.dataInicio);
  return [codigo, apelido, nomeLocataria, dataInicio, sufixo].filter(Boolean).join("_");
}

function chipStatusGeral(status) {
  const cfg = STATUS_GERAL_CONFIG[status] ?? { label: status ?? "—", cor: "#444" };
  return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.cor, color: "#fff", fontWeight: "bold" }} />;
}

function chipStatusFichas(status) {
  if (!status) return <Chip label="—" size="small" sx={{ backgroundColor: "#444", color: "#aaa" }} />;
  const cfg = STATUS_FICHAS_CONFIG[status] ?? { label: status, cor: "#444" };
  return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.cor, color: "#fff", fontWeight: "bold" }} />;
}

function chipStatusAnaliseCredito(status) {
  if (!status) return <Chip label="—" size="small" sx={{ backgroundColor: "#444", color: "#aaa" }} />;
  const cfg = STATUS_ANALISE_CREDITO_CONFIG[status] ?? { label: status, cor: "#444" };
  return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.cor, color: "#fff", fontWeight: "bold" }} />;
}

function labelFicha(f) {
  const papel = LABEL_CAMPO[f.campoProposta] ?? f.campoProposta;
  const nome = (f.tipoPessoa === "PJ" ? f.razaoSocial : f.pfNome) || "";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
      <span>{`${papel} ${f.tipoPessoa}`}</span>
      {!!nome && (
        <Box component="span" sx={{ fontSize: "0.68rem", fontWeight: 400, opacity: 0.8 }}>
          {nome.slice(0, 20)}
        </Box>
      )}
    </Box>
  );
}

// ── Sub-componente: grade de propostas/fichas ─────────────────────────────────

const GradeDePropostas = memo(function GradeDePropostas({
  gridMinimizado,
  carregandoPropostas,
  propostas,
  totalPropostas,
  paginaLista,
  porPaginaLista,
  propostaSelecionadaId,
  onVisualizar,
  onImprimir,
  setPaginaLista,
  setPorPaginaLista,
}) {
  return (
    <Collapse in={!gridMinimizado}>
      <TableContainer
        component={Paper}
        sx={{
          mb: 3,
          backgroundColor: "transparent",
          boxShadow: "none",
          overflowX: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            width: "100%",
            minWidth: 1800,
            borderCollapse: "collapse",
          }}
        >
          <TableHead>
            <TableRow>
              {/* AÇÕES */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  width: 90,
                  minWidth: 90,
                }}
              >
                Ações
              </TableCell>

              {/* CÓDIGO */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  width: 75,
                  minWidth: 75,
                }}
              >
                Cód
              </TableCell>

              {/* IMÓVEL */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  width: 170,
                  minWidth: 170,
                }}
              >
                Imóvel
              </TableCell>

              {/* COMPLEMENTO */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  width: 120,
                  minWidth: 120,
                }}
              >
                Complemento
              </TableCell>

              {/* VALOR ALUGUEL */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  width: 130,
                  minWidth: 130,
                }}
              >
                Valor Aluguel
              </TableCell>

              {/* LOCATÁRIA */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  width: 240,
                  minWidth: 220,
                  maxWidth: 240,
                }}
              >
                Locatária
              </TableCell>

              {/* DATA INÍCIO */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  width: 110,
                  minWidth: 110,
                }}
              >
                Data Início
              </TableCell>

              {/* DATA FIM */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  width: 110,
                  minWidth: 110,
                }}
              >
                Data Fim
              </TableCell>

              {/* STATUS PROPOSTA */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  minWidth: 150,
                }}
              >
                Status Proposta
              </TableCell>

              {/* STATUS FICHAS */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  minWidth: 150,
                }}
              >
                Status Fichas
              </TableCell>

              {/* STATUS ANÁLISE DE CRÉDITO */}
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  whiteSpace: "nowrap",
                  minWidth: 190,
                }}
              >
                Status Análise de Crédito
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {carregandoPropostas ? (
              <TableRow>
                <TableCell
                  colSpan={11}
                  align="center"
                  sx={{
                    borderBottom: "1px solid var(--ig-b-444444)",
                    py: 3,
                  }}
                >
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : propostas.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={11}
                  align="center"
                  sx={{
                    color: "var(--ig-t-888888)",
                    py: 3,
                    borderBottom: "1px solid var(--ig-b-444444)",
                  }}
                >
                  Nenhuma proposta encontrada.
                </TableCell>
              </TableRow>
            ) : (
              propostas.map((p) => {
                const locatarias = (p.fichaPessoas ?? []).filter((f) => f.campoProposta === "Locataria");

                const nomesLocatarias = locatarias
                  .map((loc) => (loc.tipoPessoa === "PJ" ? loc.razaoSocial || "" : loc.pfNome || ""))
                  .filter(Boolean)
                  .join(" / ");

                const imovel = [p.imovel?.codigoMatricula, p.imovel?.apelido].filter(Boolean).join(" – ") || "—";

                const valorAluguel =
                  p.vAluguelMensal !== null && p.vAluguelMensal !== undefined && p.vAluguelMensal !== "" ? `R$ ${toMoeda(p.vAluguelMensal)}` : "—";

                return (
                  <TableRow
                    key={p.id}
                    sx={{
                      cursor: "pointer",

                      "&:hover": {
                        backgroundColor: "var(--ig-f-2a2a2a)",
                      },

                      backgroundColor: propostaSelecionadaId === p.id ? "rgba(25,152,168,0.08)" : "transparent",
                    }}
                    onClick={() => onVisualizar(p.id)}
                  >
                    {/* AÇÕES */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                        width: 90,
                        minWidth: 90,
                      }}
                    >
                      <Tooltip title="Visualizar">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onVisualizar(p.id);
                          }}
                          sx={{
                            backgroundColor: "#1998a8",
                            color: "#fff",
                            borderRadius: "4px",
                            mx: 0.25,
                            width: "auto",

                            "&:hover": {
                              backgroundColor: "#1565c0",
                            },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Imprimir Fichas">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onImprimir(p.id);
                          }}
                          sx={{
                            backgroundColor: "#e65100",
                            color: "#fff",
                            borderRadius: "4px",
                            mx: 0.25,
                            width: "auto",

                            "&:hover": {
                              backgroundColor: "#bf360c",
                            },
                          }}
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>

                    {/* CÓDIGO */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        color: "var(--ig-t-aaaaaa)",
                        whiteSpace: "nowrap",
                        width: 75,
                        minWidth: 75,
                      }}
                    >
                      {p.codigo ?? `PC${p.id}`}
                    </TableCell>

                    {/* IMÓVEL */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        color: "var(--ig-t-ffffff)",
                        whiteSpace: "nowrap",
                        fontWeight: 600,
                        width: 170,
                        minWidth: 170,
                      }}
                    >
                      {imovel}
                    </TableCell>

                    {/* COMPLEMENTO */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        color: "var(--ig-t-cccccc)",
                        whiteSpace: "nowrap",
                        width: 120,
                        minWidth: 120,
                      }}
                    >
                      {p.complementoLocacao || "—"}
                    </TableCell>

                    {/* VALOR ALUGUEL */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        color: "var(--ig-t-cccccc)",
                        whiteSpace: "nowrap",
                        width: 130,
                        minWidth: 130,
                      }}
                    >
                      {valorAluguel}
                    </TableCell>

                    {/* LOCATÁRIA */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        width: 240,
                        minWidth: 220,
                        maxWidth: 240,
                      }}
                    >
                      <Typography
                        title={nomesLocatarias || "—"}
                        sx={{
                          color: "var(--ig-t-cccccc)",
                          fontSize: "0.78rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: 220,
                          maxWidth: 220,
                        }}
                      >
                        {nomesLocatarias || "—"}
                      </Typography>
                    </TableCell>

                    {/* DATA INÍCIO */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        color: "var(--ig-t-cccccc)",
                        whiteSpace: "nowrap",
                        width: 110,
                        minWidth: 110,
                      }}
                    >
                      {fmtData(p.dataInicio)}
                    </TableCell>

                    {/* DATA FIM */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        color: "var(--ig-t-cccccc)",
                        whiteSpace: "nowrap",
                        width: 110,
                        minWidth: 110,
                      }}
                    >
                      {fmtData(p.dataFim)}
                    </TableCell>

                    {/* STATUS PROPOSTA */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                        minWidth: 150,
                      }}
                    >
                      {chipStatusGeral(p.statusGeral ?? "EmDigitacao")}
                    </TableCell>

                    {/* STATUS FICHAS */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                        minWidth: 150,
                      }}
                    >
                      {chipStatusFichas(p.statusFichas)}
                    </TableCell>

                    {/* STATUS ANÁLISE DE CRÉDITO */}
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                        minWidth: 190,
                      }}
                    >
                      {chipStatusAnaliseCredito(p.statusAnaliseCredito)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalPropostas}
          page={paginaLista}
          onPageChange={(_, p) => setPaginaLista(p)}
          rowsPerPage={porPaginaLista}
          onRowsPerPageChange={(e) => {
            setPorPaginaLista(parseInt(e.target.value, 10));
            setPaginaLista(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Propostas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{
            color: "var(--ig-t-ffffff)",
            borderTop: "1px solid var(--ig-b-444444)",

            "& .MuiTablePagination-selectIcon": {
              color: "var(--ig-t-ffffff)",
            },

            "& .MuiTablePagination-actions button": {
              color: "var(--ig-t-ffffff)",
              width: "auto",
            },

            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </TableContainer>
    </Collapse>
  );
});
// ── Componente principal ──────────────────────────────────────────────────────

export default function FichaCadastro() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Permissões
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tipoUsuario = decoded.tipo?.toString().trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    } catch (_) {}
  }
  let idUsuarioLogado = null;
  if (token) {
    try {
      idUsuarioLogado = jwtDecode(token).id ?? null;
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  // ── Busca de propostas (quando nenhuma está selecionada)
  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [propostas, setPropostas] = useState([]);
  const [totalPropostas, setTotalPropostas] = useState(0);
  const [paginaLista, setPaginaLista] = useState(0);
  const [porPaginaLista, setPorPaginaLista] = useState(5);
  const [carregandoPropostas, setCarregandoPropostas] = useState(false);
  const [gridMinimizado, setGridMinimizado] = useState(false);
  const [fichasParaImprimir, setFichasParaImprimir] = useState(null); // { proposta, fichas }
  const printFrameRef = useRef(null);

  // ── Filtro vindo do Dashboard (pendência do fluxo de aprovação de Fichas/Análise de Crédito)
  const [filtroDashboard, setFiltroDashboard] = useState(() => {
    const statusAprovacao = searchParams.get("statusAprovacao");
    const processo = searchParams.get("processo") === "AnaliseCredito" ? "AnaliseCredito" : "Fichas";
    const minhasPropostas = searchParams.get("minhasPropostas") === "true";
    const comFichas = searchParams.get("comFichas") === "true";
    const comAnaliseCredito = searchParams.get("comAnaliseCredito") === "true";
    const escopoGeral = searchParams.get("escopo") === "geral";
    if (statusAprovacao) return { tipo: "statusAprovacao", valor: statusAprovacao, processo, geral: escopoGeral };
    if (minhasPropostas) return { tipo: "minhasPropostas", comFichas, comAnaliseCredito };
    if (comFichas) return { tipo: "comFichas" };
    if (comAnaliseCredito) return { tipo: "comAnaliseCredito" };
    return null;
  });

  // ── Proposta + fichas carregadas
  const [propostaInfo, setPropostaInfo] = useState(null);
  const [fichas, setFichas] = useState([]);
  const [formFichas, setFormFichas] = useState([]);
  const [fichaIdx, setFichaIdx] = useState(0);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [ultimoCnpjBuscado, setUltimoCnpjBuscado] = useState("");
  const primeiroCampoRef = useRef(null);
  const focarPrimeiroCampo = () => setTimeout(() => primeiroCampoRef.current?.focus(), 300);
  const [carregandoFichas, setCarregandoFichas] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [errosPorFicha, setErrosPorFicha] = useState({});
  const [ehAprovador, setEhAprovador] = useState(false);
  const [somenteLeitura, setSomenteLeitura] = useState(true);
  const [aba, setAba] = useState("dados");

  // ── Snackbar
  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = useCallback((msg, severity = "success") => setSnack({ aberto: true, msg, severity }), []);

  // ── Documentos do checklist
  const [docsPorFicha, setDocsPorFicha] = useState({});

  const [uploadState, setUploadState] = useState({});

  const [uploadando, setUploadando] = useState({});

  // ============================================================
  // JUSTIFICATIVAS DOS DOCUMENTOS AUSENTES
  // ============================================================

  const [dlgJustificativasDocs, setDlgJustificativasDocs] = useState({
    aberto: false,

    // Cada posição representa um checklist separado.
    grupos: [],

    // Justificativas digitadas:
    // chave -> texto
    valores: {},

    // Aba atualmente aberta no modal.
    abaAtiva: 0,

    salvando: false,
  });

  // ============================================================
  // ABA DE JUSTIFICATIVA VISÍVEL PELO APROVADOR
  // ============================================================

  const [abaJustificativaAprovacao, setAbaJustificativaAprovacao] = useState(0);

  // ── fichas (campos obrigatórios de texto) — bloqueia o envio para aprovação; documentos não bloqueiam
  const pendenciasPorFicha = useMemo(
    () =>
      formFichas
        .map((f) => ({
          nome: f.tipoPessoa === "PJ" ? f.razaoSocial || "Pessoa Jurídica sem nome" : f.pfNome || "Pessoa Física sem nome",
          pendencias: pendenciasDaFicha(f),
        }))
        .filter((p) => p.pendencias.length > 0),
    [formFichas],
  );
  const todasFichasCompletas = formFichas.length > 0 && pendenciasPorFicha.length === 0;

  // ── Aprovação das Fichas
  const [aprovacaoFichas, setAprovacaoFichas] = useState(null); // PropostaAprovacao do fluxo Fichas
  const [autorizacoesFichas, setAutorizacoesFichas] = useState([]); // PropostaAprovacaoAutorizacao
  const [historicosFichas, setHistoricosFichas] = useState([]);
  const [usuariosAprovadores, setUsuariosAprovadores] = useState([]);
  const [enviandoFichas, setEnviandoFichas] = useState(false);
  const [processandoFichas, setProcessandoFichas] = useState(false);
  // diálogos aprovação fichas
  const [dlgReprovarFichas, setDlgReprovarFichas] = useState({ aberto: false, motivo: "" });
  const [dlgQuestionarFichas, setDlgQuestionarFichas] = useState({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null });
  const [dlgResponderFichas, setDlgResponderFichas] = useState({ aberto: false, resposta: "", pergunta: "", arquivo: null });
  const [dlgRevisarFichas, setDlgRevisarFichas] = useState({ aberto: false, motivo: "" });

  // ── Análise de Crédito
  const [docsAnalise, setDocsAnalise] = useState([]);
  const [aprovacaoAnalise, setAprovacaoAnalise] = useState(null);
  const [autorizacoesAnalise, setAutorizacoesAnalise] = useState([]);
  const [historicosAnalise, setHistoricosAnalise] = useState([]);
  const [uploadandoAnalise, setUploadandoAnalise] = useState(false);
  const [arquivoAnalise, setArquivoAnalise] = useState(null);
  const [nomeAnalise, setNomeAnalise] = useState("");
  const [observacaoAnaliseCredito, setObservacaoAnaliseCredito] = useState("");
  const [enviandoAnalise, setEnviandoAnalise] = useState(false);
  const [processandoAnalise, setProcessandoAnalise] = useState(false);
  // diálogos aprovação análise
  const [dlgReprovarAnalise, setDlgReprovarAnalise] = useState({ aberto: false, motivo: "" });
  const [dlgQuestionarAnalise, setDlgQuestionarAnalise] = useState({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null });
  const [dlgResponderAnalise, setDlgResponderAnalise] = useState({ aberto: false, resposta: "", pergunta: "", arquivo: null });
  const [dlgRevisarAnalise, setDlgRevisarAnalise] = useState({ aberto: false, motivo: "" });

  // ── Sincronizar dados de aprovação a partir do objeto proposta completo
  const sincronizarAprovacao = useCallback((p) => {
    if (!p) return;

    const aprovs = p.aprovacoes ?? [];
    const auts = p.autorizacoes ?? [];
    const hists = p.historicos ?? [];

    // Fichas
    const aprovFichas = aprovs.filter((a) => a.processoTipo === "Fichas");

    setAprovacaoFichas(aprovFichas[0] ?? null);

    setAutorizacoesFichas(auts.filter((a) => a.processoTipo === "Fichas").sort((a, b) => a.ordem - b.ordem));

    setHistoricosFichas(hists.filter((h) => h.processoTipo === "Fichas"));

    // Análise de crédito
    const aprovAnalise = aprovs.filter((a) => a.processoTipo === "AnaliseCredito");

    setAprovacaoAnalise(aprovAnalise[0] ?? null);

    setAutorizacoesAnalise(auts.filter((a) => a.processoTipo === "AnaliseCredito").sort((a, b) => a.ordem - b.ordem));

    setHistoricosAnalise(hists.filter((h) => h.processoTipo === "AnaliseCredito"));

    // Docs análise de crédito
    setDocsAnalise(p.analiseCreditoDocs ?? []);

    // Observação da análise de crédito
    if (Object.prototype.hasOwnProperty.call(p, "observacaoAnaliseCredito")) {
      setObservacaoAnaliseCredito(p.observacaoAnaliseCredito ?? "");
    }
  }, []);

  // ── Recarregar dados completos da proposta (aprovações, status, etc.)
  const recarregarProposta = useCallback(
    (propostaId) => {
      const tk = localStorage.getItem("token");
      return fetch(`/api/propostas/${propostaId}`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((p) => {
          setPropostaInfo((prev) => ({ ...prev, ...p }));
          sincronizarAprovacao(p);
          return p;
        });
    },
    [sincronizarAprovacao],
  );

  // ── Carregar fichas de uma proposta (abaAlvo: abre direto numa aba específica, ex.: vindo do dashboard)
  const carregarFichas = useCallback(
    (propostaId, abaAlvo) => {
      const tk = localStorage.getItem("token");

      setCarregandoFichas(true);

      // Evita manter a observação de uma proposta anteriormente aberta
      setObservacaoAnaliseCredito("");

      const fichasP = fetch(`/api/ficha-pessoa/por-proposta/${propostaId}`, {
        headers: { Authorization: `Bearer ${tk}` },
      }).then((r) => (r.ok ? r.json() : Promise.reject(r.status)));

      const propostaP = fetch(`/api/propostas/${propostaId}`, {
        headers: { Authorization: `Bearer ${tk}` },
      }).then((r) => (r.ok ? r.json() : Promise.reject(r.status)));

      const usuariosAprovadoresP = fetch("/api/propostas/usuarios-aprovadores", {
        headers: { Authorization: `Bearer ${tk}` },
      }).then((r) => (r.ok ? r.json() : []));

      Promise.all([fichasP, propostaP, usuariosAprovadoresP])
        .then(([{ proposta, fichas: fs, ehAprovador: ea }, propostaCompleta, usuariosAprov]) => {
          const fichasValidas = fs.filter((f) => permiteFichaCadastral(f.campoProposta));
          setPropostaInfo({
            ...proposta,
            ...propostaCompleta,
            id: propostaId,
          });

          setFichas(fichasValidas);

          setFormFichas(
            fichasValidas.map((f) => {
              const form = fichaParaForm(f);

              const garantiaNormalizadaCarregamento = String(propostaCompleta?.garantia || "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .trim()
                .toUpperCase();

              const temGarantiaImobiliaria = garantiaNormalizadaCarregamento
                .split(",")
                .map((g) => g.trim())
                .includes("IMOBILIARIA");

              const fiadorComImovel =
                String(propostaCompleta?.fiadorTipoGarantia || "")
                  .trim()
                  .toUpperCase() === "COM_IMOVEL";

              // =========================================================
              // FIADOR COM IMÓVEL
              // O link informado na PC vem automaticamente para a FC.
              // Se a FC já possuir um valor próprio salvo, ele é preservado.
              // =========================================================
              if (f.campoProposta === "Fiador" && fiadorComImovel && !form.linkGoogleImovel) {
                form.linkGoogleImovel = propostaCompleta?.fiadorImovelUrlGoogleMaps || "";
              }

              // =========================================================
              // GARANTIA IMOBILIÁRIA
              // O link informado na PC vem automaticamente para a FC
              // da Locatária.
              // Se a FC já possuir um valor próprio salvo, ele é preservado.
              // =========================================================
              if (f.campoProposta === "Locataria" && temGarantiaImobiliaria && !form.linkGoogleImovel) {
                form.linkGoogleImovel = propostaCompleta?.garantiaUrlGoogleMaps || "";
              }

              return form;
            }),
          );

          setFichaIdx(0);
          setSomenteLeitura(true);
          setAba(abaAlvo ?? "dados");
          setEhAprovador(ea ?? false);
          const docsMap = {};

          fichasValidas.forEach((f) => {
            docsMap[f.id] = Array.isArray(f.documentos) ? f.documentos : [];
          });

          setDocsPorFicha(docsMap);

          sincronizarAprovacao(propostaCompleta);
          setUsuariosAprovadores(Array.isArray(usuariosAprov) ? usuariosAprov : []);
          focarPrimeiroCampo();
        })
        .catch(() => mostrarSnack("Erro ao carregar fichas.", "error"))
        .finally(() => setCarregandoFichas(false));
    },
    [sincronizarAprovacao, mostrarSnack],
  );

  // ── Visualizar (grid → form): colapsa o grid e rola pro form enquanto carrega.
  // Vindo de um filtro do dashboard, abre direto na aba de aprovação do processo pendente.
  const handleVisualizarProposta = useCallback(
    (propostaId) => {
      setGridMinimizado(true);
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      const abaAlvo =
        filtroDashboard?.tipo === "statusAprovacao" ? (filtroDashboard.processo === "AnaliseCredito" ? "aprovacaoAnalise" : "aprovacao") : undefined;
      carregarFichas(propostaId, abaAlvo);
    },
    [carregarFichas, filtroDashboard],
  );

  // ── Ler param da URL na montagem
  useEffect(() => {
    const id = searchParams.get("proposta");
    if (id && podeVisualizar) handleVisualizarProposta(Number(id));
  }, [searchParams, podeVisualizar, handleVisualizarProposta]);

  // ── Voltar da visualização de uma ficha para o grid de propostas/fichas
  const handleVoltarParaLista = () => {
    setPropostaInfo(null);
    setFichas([]);
    setFormFichas([]);
    setFichaIdx(0);
    setSomenteLeitura(true);
    setAba("dados");
    setDocsPorFicha({});
    setUploadState({});
    setSearchParams({}, { replace: true });
    setGridMinimizado(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    carregarListaPropostas();
  };

  // ── Carregar grid de propostas/fichas (lista completa, com filtro + paginação)
  const carregarListaPropostas = useCallback(() => {
    const tk = localStorage.getItem("token");
    setCarregandoPropostas(true);
    const params = new URLSearchParams({ filtro, pagina: paginaLista, porPagina: porPaginaLista });
    if (filtroDashboard?.tipo === "statusAprovacao") {
      params.set("statusAprovacao", filtroDashboard.valor);
      params.set("processoPendencia", filtroDashboard.processo);
      if (filtroDashboard.geral) params.set("escopo", "geral");
    }
    if (filtroDashboard?.tipo === "minhasPropostas") {
      params.set("minhasPropostas", "true");
      if (filtroDashboard.comFichas) params.set("comFichas", "true");
      if (filtroDashboard.comAnaliseCredito) params.set("comAnaliseCredito", "true");
    }
    if (filtroDashboard?.tipo === "comFichas") params.set("comFichas", "true");
    if (filtroDashboard?.tipo === "comAnaliseCredito") params.set("comAnaliseCredito", "true");
    fetch(`/api/propostas?${params}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then(({ data, total: t }) => {
        setPropostas(Array.isArray(data) ? data : []);
        setTotalPropostas(t ?? 0);
      })
      .catch(() => mostrarSnack("Erro ao buscar propostas.", "error"))
      .finally(() => setCarregandoPropostas(false));
  }, [filtro, paginaLista, porPaginaLista, mostrarSnack, filtroDashboard]);

  useEffect(() => {
    if (podeVisualizar) carregarListaPropostas();
  }, [podeVisualizar, carregarListaPropostas]);

  // ── Pesquisa automática: aplica o filtro digitado após o usuário parar de digitar
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPaginaLista(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPaginaLista(0);
  };

  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPaginaLista(0);
  };

  // =======================
  // COPIAR
  // =======================
  const handleCopiar = async () => {
    const texto = propostas
      .map((p) => {
        const codigo = p.codigo ?? `PC${p.id}`;

        const tipoLocacao = p.tipoLocacao || "";

        const locatarias = (p.fichaPessoas ?? [])
          .filter((f) => f.campoProposta === "Locataria")
          .map((loc) => {
            const documento = (loc.tipoPessoa === "PJ" ? loc.cnpj || "" : loc.cpf || "") || descreverDocumentoEstrangeiro(loc);

            const nome = loc.tipoPessoa === "PJ" ? loc.razaoSocial || "" : loc.pfNome || "";

            return [documento, loc.tipoPessoa || "", nome].filter(Boolean).join(" - ");
          })
          .join(" / ");

        const imovel = p.imovel?.codigoMatricula || p.imovel?.apelido || "";

        const endereco = p.enderecoImovel || "";

        const statusProposta = STATUS_GERAL_CONFIG[p.statusGeral ?? "EmDigitacao"]?.label ?? p.statusGeral ?? "";

        const statusFichas = STATUS_FICHAS_CONFIG[p.statusFichas]?.label ?? p.statusFichas ?? "";

        const statusAnaliseCredito = STATUS_ANALISE_CREDITO_CONFIG[p.statusAnaliseCredito]?.label ?? p.statusAnaliseCredito ?? "";

        return [codigo, tipoLocacao, locatarias, imovel, endereco, statusProposta, statusFichas, statusAnaliseCredito].join(" - ");
      })
      .join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);

    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  // =======================
  // CSV
  // =======================
  // =======================
  // CSV
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = [
      "Código",
      "Imóvel",
      "Complemento",
      "Valor Aluguel",
      "Locatária",
      "Data Início",
      "Data Fim",
      "Status Proposta",
      "Status Fichas",
      "Status Análise de Crédito",
    ];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = propostas.map((p) => {
      const codigo = p.codigo ?? `PC${p.id}`;

      const imovel = [p.imovel?.codigoMatricula, p.imovel?.apelido].filter(Boolean).join(" – ") || "";

      const complemento = p.complementoLocacao || "";

      const valorAluguel = p.vAluguelMensal !== null && p.vAluguelMensal !== undefined && p.vAluguelMensal !== "" ? `R$ ${toMoeda(p.vAluguelMensal)}` : "";

      const locatarias = (p.fichaPessoas ?? [])
        .filter((f) => f.campoProposta === "Locataria")
        .map((loc) => (loc.tipoPessoa === "PJ" ? loc.razaoSocial || "" : loc.pfNome || ""))
        .filter(Boolean)
        .join(" / ");

      const dataInicio = fmtData(p.dataInicio);

      const dataFim = fmtData(p.dataFim);

      const statusProposta = STATUS_GERAL_CONFIG[p.statusGeral ?? "EmDigitacao"]?.label ?? p.statusGeral ?? "";

      const statusFichas = STATUS_FICHAS_CONFIG[p.statusFichas]?.label ?? p.statusFichas ?? "";

      const statusAnaliseCredito = STATUS_ANALISE_CREDITO_CONFIG[p.statusAnaliseCredito]?.label ?? p.statusAnaliseCredito ?? "";

      return [codigo, imovel, complemento, valorAluguel, locatarias, dataInicio, dataFim, statusProposta, statusFichas, statusAnaliseCredito].map(csvEscape);
    });

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "fichas-de-cadastro.csv");

    document.body.appendChild(link);

    link.click();

    link.remove();

    URL.revokeObjectURL(url);
  };

  const handleLimparFiltroDashboard = () => {
    setFiltroDashboard(null);
    setPaginaLista(0);
    setSearchParams({});
  };

  // ── Imprimir dados das fichas de cadastro da proposta
  const handleImprimirFichas = useCallback(
    async (propostaId) => {
      try {
        const tk = localStorage.getItem("token");
        const [resProposta, resFichas] = await Promise.all([
          fetch(`/api/propostas/${propostaId}`, { headers: { Authorization: `Bearer ${tk}` } }),
          fetch(`/api/ficha-pessoa/por-proposta/${propostaId}`, { headers: { Authorization: `Bearer ${tk}` } }),
        ]);
        if (!resProposta.ok || !resFichas.ok) {
          mostrarSnack("Erro ao carregar fichas para impressão.", "error");
          return;
        }
        const proposta = await resProposta.json();
        const { fichas: fichasData } = await resFichas.json();

        const fichasValidasImpressao = (fichasData ?? []).filter((f) => permiteFichaCadastral(f.campoProposta));

        setFichasParaImprimir({
          proposta,
          fichas: fichasValidasImpressao,
        });
        setTimeout(() => {
          const el = document.getElementById("print-fichas");
          const iframe = printFrameRef.current;
          if (!el || !iframe) {
            window.print();
            return;
          }
          const nomeArquivo = nomeArquivoImpressao(proposta, "FC");
          const doc = iframe.contentDocument || iframe.contentWindow.document;
          doc.open();
          doc.write(
            `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${nomeArquivo}</title>` +
              `<style>@page{size:A4 portrait;margin:0}body{margin:0;padding:0;background:#fff}` +
              `*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}</style>` +
              `</head><body>${el.outerHTML}</body></html>`,
          );
          doc.close();
          iframe.contentWindow.focus();
          // Alguns navegadores usam o título da aba (não o do iframe) como nome sugerido
          // ao "Salvar como PDF" — troca temporariamente pra garantir o nome certo.
          const tituloAnterior = document.title;
          document.title = nomeArquivo;
          window.addEventListener(
            "afterprint",
            () => {
              document.title = tituloAnterior;
            },
            { once: true },
          );
          iframe.contentWindow.print();
        }, 400);
      } catch {
        mostrarSnack("Erro de conexão.", "error");
      }
    },
    [mostrarSnack],
  );

  // ============================================================
  // TEMPLATE FICHA DE CADASTRO
  // ============================================================

  const handleTemplateFC = useCallback(() => {
    try {
      setFichasParaImprimir({
        modoTemplate: true,

        proposta: {
          codigo: "",
          enderecoImovel: "",
        },

        fichas: [],
      });

      setTimeout(() => {
        const el = document.getElementById("print-fichas");

        const iframe = printFrameRef.current;

        if (!el || !iframe) {
          mostrarSnack("Não foi possível gerar o template da Ficha de Cadastro.", "error");

          return;
        }

        const nomeArquivo = "TEMPLATE FC";
        const doc = iframe.contentDocument || iframe.contentWindow.document;

        doc.open();

        doc.write(
          `<!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8">

              <title>
                ${nomeArquivo}
              </title>

              <style>
                @page {
                  size: A4 portrait;
                  margin: 0;
                }

                body {
                  margin: 0;
                  padding: 0;
                  background: #fff;
                }

                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                  color-adjust: exact !important;
                }
              </style>
            </head>

            <body>
              ${el.outerHTML}
            </body>
          </html>`,
        );

        doc.close();

        iframe.contentWindow.focus();

        // Alguns navegadores usam o título da aba (não o do iframe) como nome sugerido
        // ao "Salvar como PDF" — troca temporariamente pra garantir o nome certo.
        const tituloAnterior = document.title;
        document.title = nomeArquivo;
        window.addEventListener(
          "afterprint",
          () => {
            document.title = tituloAnterior;
          },
          { once: true },
        );

        iframe.contentWindow.print();
      }, 400);
    } catch (erro) {
      console.error("Erro ao gerar TEMPLATE FC:", erro);

      mostrarSnack("Erro ao gerar template da Ficha de Cadastro.", "error");
    }
  }, [mostrarSnack]);

  // ── Atualizar campo do form da ficha ativa
  const setField = useCallback(
    (field, value) => {
      setFormFichas((prev) => prev.map((f, i) => (i === fichaIdx ? { ...f, [field]: value } : f)));
    },
    [fichaIdx],
  );

  // ── CNPJ preenchido (PJ) → busca automática de razão social + endereço
  const handleCnpjChange = async (valorDigitado) => {
    if (ro) return;
    const masked = mascararCNPJ(valorDigitado);
    setField("cnpj", masked);

    const digitos = limparCnpj(masked);
    if (digitos.length !== 14 || digitos === ultimoCnpjBuscado) return;

    setUltimoCnpjBuscado(digitos);
    setBuscandoCnpj(true);
    const idxBuscado = fichaIdx;
    const resultado = await buscarDadosCnpj(masked);
    setBuscandoCnpj(false);

    if (resultado.ok) {
      setFormFichas((prev) =>
        prev.map((f, i) =>
          i === idxBuscado
            ? {
                ...f,
                razaoSocial: resultado.razaoSocial.toUpperCase(),
                endereco: resultado.endereco ? resultado.endereco.toUpperCase() : f.endereco,
                numero: resultado.numero ? resultado.numero.toUpperCase() : f.numero,
                bairro: resultado.bairro ? resultado.bairro.toUpperCase() : f.bairro,
                cidade: resultado.cidade ? resultado.cidade.toUpperCase() : f.cidade,
                uf: resultado.uf ? resultado.uf.toUpperCase() : f.uf,
                cep: resultado.cep || f.cep,
                complemento: resultado.complemento ? resultado.complemento.toUpperCase() : f.complemento,
              }
            : f,
        ),
      );
    } else if (resultado.motivo !== "invalido") {
      mostrarSnack(resultado.mensagem, ["nao_encontrado", "vazio"].includes(resultado.motivo) ? "warning" : "error");
    }
  };

  // ── Salvar ficha ativa
  const handleCancelarEdicaoFicha = () => {
    setFormFichas((prev) => prev.map((f, i) => (i === fichaIdx ? fichaParaForm(fichas[fichaIdx]) : f)));
    setErrosPorFicha((prev) => ({ ...prev, [fichaIdx]: {} }));
    setSomenteLeitura(true);
  };

  const handleSalvar = () => {
    const fichaId = fichas[fichaIdx]?.id;
    if (!fichaId) return;
    const tk = localStorage.getItem("token");
    setSalvando(true);
    fetch(`/api/ficha-pessoa/${fichaId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify(formFichas[fichaIdx]),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro ao salvar.", "error");
          return;
        }
        mostrarSnack("Ficha salva com sucesso!");
        setSomenteLeitura(true);
        // Atualiza o label da aba com os novos dados
        const { ficha } = await res
          .clone()
          .json()
          .catch(() => ({}));
        if (ficha) {
          setFichas((prev) => prev.map((f, i) => (i === fichaIdx ? { ...f, ...ficha } : f)));
        }
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setSalvando(false));
  };

  // ── documentos ──────────────────────────────────────────────────

  const recarregarDocs = useCallback(
    (fId) => {
      const tk = localStorage.getItem("token");
      fetch(`/api/ficha-pessoa/${fId}/documentos`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((docs) => setDocsPorFicha((prev) => ({ ...prev, [fId]: docs })))
        .catch(() => mostrarSnack("Erro ao carregar documentos.", "error"));
    },
    [mostrarSnack],
  );

  const setUploadField = useCallback((fId, tipo, campo, valor) => {
    setUploadState((prev) => ({
      ...prev,
      [fId]: {
        ...(prev[fId] ?? {}),
        [tipo]: { ...(prev[fId]?.[tipo] ?? { arquivo: null, nome: "" }), [campo]: valor },
      },
    }));
  }, []);

  // Link público de preenchimento da ficha. Não existe endpoint de "gerar": o
  // token vem calculado em `listarPorProposta`, derivado do id. Copiar duas vezes
  // devolve URLs diferentes (o IV é aleatório) e AMBAS abrem a mesma ficha —
  // por consequência, um link entregue à pessoa errada não tem como ser revogado.
  const copiarLinkPublicoFicha = useCallback(
    async (ficha) => {
      if (!ficha?.id) {
        mostrarSnack("Ficha não identificada.", "error");
        return;
      }

      const tk = localStorage.getItem("token");

      try {
        const emitir = async (renovar = false) => {
          const resposta = await fetch(`/api/ficha-pessoa/${ficha.id}/link-publico`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${tk}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ renovar }),
          });

          const dados = await resposta.json().catch(() => ({}));

          return { resposta, dados };
        };

        // Primeira emissão ou recuperação do link ainda válido.
        let resultado = await emitir(false);

        // Um link vencido só é renovado mediante o clique
        // explícito do usuário no botão COPIAR LINK.
        if (resultado.resposta.status === 409 && resultado.dados.motivo === "renovacao_necessaria") {
          resultado = await emitir(true);
        }

        if (!resultado.resposta.ok) {
          throw new Error(resultado.dados.erro || "Não foi possível obter o link.");
        }

        const dados = resultado.dados;

        if (!dados.linkPublicoToken) {
          throw new Error("O servidor não retornou um link válido.");
        }

        // Mantém os dados da ficha sincronizados com a emissão.
        setFichas((anteriores) =>
          anteriores.map((item) =>
            item.id === ficha.id
              ? {
                  ...item,
                  linkPublicoToken: dados.linkPublicoToken,
                  linkPublicoEmitidoEm: dados.emitidoEm,
                  linkPublicoExpiraEm: dados.expiraEm,
                }
              : item,
          ),
        );

        const url = `${window.location.origin}/ficha-publica/${dados.linkPublicoToken}`;

        const copiado = await copiarTexto(url);

        mostrarSnack(copiado ? "Link copiado. Envie para a pessoa preencher a ficha." : "Não foi possível copiar o link.", copiado ? "success" : "error");
      } catch (erro) {
        mostrarSnack(erro.message || "Erro ao copiar link público.", "error");
      }
    },
    [mostrarSnack],
  );

  const handleUploadDoc = useCallback(
    (fId, tipo, obrigatorio) => {
      const uState = uploadState[fId]?.[tipo];
      if (!uState?.arquivo) return mostrarSnack("Selecione um arquivo.", "warning");
      const tk = localStorage.getItem("token");
      const chave = `${fId}_${tipo}`;
      setUploadando((prev) => ({ ...prev, [chave]: true }));
      const fd = new FormData();
      fd.append("arquivo", uState.arquivo);
      fd.append("tipo", tipo);
      fd.append("obrigatorio", String(obrigatorio));
      fd.append("nomeArquivo", uState.nome.trim() || uState.arquivo.name);
      fetch(`/api/ficha-pessoa/${fId}/documentos`, { method: "POST", headers: { Authorization: `Bearer ${tk}` }, body: fd })
        .then(async (r) => {
          if (!r.ok) {
            const d = await r.json();
            throw new Error(d.erro || "Erro ao enviar.");
          }
          setUploadField(fId, tipo, "arquivo", null);
          setUploadField(fId, tipo, "nome", "");
          recarregarDocs(fId);
        })
        .catch((e) => mostrarSnack(e.message || "Erro ao enviar arquivo.", "error"))
        .finally(() => setUploadando((prev) => ({ ...prev, [chave]: false })));
    },
    [uploadState, setUploadField, recarregarDocs, mostrarSnack],
  );

  const handleDeleteDoc = useCallback(
    (fId, docId) => {
      const tk = localStorage.getItem("token");
      fetch(`/api/ficha-pessoa/${fId}/documentos/${docId}`, { method: "DELETE", headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => {
          if (r.ok) recarregarDocs(fId);
          else mostrarSnack("Erro ao excluir.", "error");
        })
        .catch(() => mostrarSnack("Erro ao excluir.", "error"));
    },
    [recarregarDocs, mostrarSnack],
  );

  const handleDownloadDoc = useCallback(
    (fId, docId, nomeArquivo) => {
      const tk = localStorage.getItem("token");
      fetch(`/api/ficha-pessoa/${fId}/documentos/${docId}/download`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.blob() : Promise.reject("Erro ao baixar.")))
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = nomeArquivo;
          a.click();
          URL.revokeObjectURL(url);
        })
        .catch(() => mostrarSnack("Erro ao baixar arquivo.", "error"));
    },
    [mostrarSnack],
  );

  const handleVisualizarDoc = useCallback(
    (fId, docId) => {
      const tk = localStorage.getItem("token");
      fetch(`/api/ficha-pessoa/${fId}/documentos/${docId}/download`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.blob() : Promise.reject("Erro ao visualizar.")))
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        })
        .catch(() => mostrarSnack("Erro ao visualizar arquivo.", "error"));
    },
    [mostrarSnack],
  );

  // ============================================================
  // DOCUMENTOS AUSENTES DAS FICHAS
  // Cada checklist vira um grupo independente.
  // ============================================================

  const montarAusenciasDocumentais = useCallback(() => {
    const gruposAusentes = [];

    const valores = {};

    formFichas.forEach((fichaForm, index) => {
      const fichaBanco = fichas[index];

      const fichaIdLocal = fichaBanco?.id;

      if (!fichaIdLocal) {
        return;
      }

      // =====================================================
      // CHECKLISTS APLICÁVEIS PARA ESTA FICHA
      // =====================================================

      const gruposChecklist = obterGruposChecklistDocumentosFicha(fichaForm, propostaInfo);

      const documentos = docsPorFicha[fichaIdLocal] ?? [];

      const justificativasExistentes = Array.isArray(fichaBanco?.justificativasDocumentos) ? fichaBanco.justificativasDocumentos : [];

      const nomeFicha = fichaForm.tipoPessoa === "PJ" ? fichaForm.razaoSocial || "Pessoa Jurídica" : fichaForm.pfNome || "Pessoa Física";

      const papel = LABEL_CAMPO[fichaForm.campoProposta] || fichaForm.campoProposta;

      // =====================================================
      // ANALISAR CADA CHECKLIST SEPARADAMENTE
      // =====================================================

      gruposChecklist.forEach((grupoChecklist) => {
        const itensAusentes = [];

        grupoChecklist.itens.forEach((item) => {
          const temDocumento = documentos.some((doc) => String(doc.tipo) === String(item.tipo));

          // Documento anexado:
          // não precisa justificativa.
          if (temDocumento) {
            return;
          }

          // =================================================
          // CHAVE ÚNICA SOMENTE PARA O FRONTEND
          // =================================================

          const chave = `${fichaIdLocal}::${grupoChecklist.chave}::${item.tipo}`;

          const justificativaExistente = justificativasExistentes.find((justificativa) => String(justificativa.tipoDocumento) === String(item.tipo));

          itensAusentes.push({
            chave,

            fichaId: fichaIdLocal,

            fichaIndex: index,

            tipoDocumento: item.tipo,

            label: item.label,
          });

          valores[chave] = justificativaExistente?.justificativa || "";
        });

        // =================================================
        // SÓ CRIA ABA SE ESSE CHECKLIST TIVER AUSÊNCIA
        // =================================================

        if (itensAusentes.length === 0) {
          return;
        }

        gruposAusentes.push({
          chave: `${fichaIdLocal}::${grupoChecklist.chave}`,

          fichaId: fichaIdLocal,

          fichaIndex: index,

          fichaNome: `${papel} ${fichaForm.tipoPessoa} - ${nomeFicha}`,

          titulo: grupoChecklist.titulo,

          itens: itensAusentes,
        });
      });
    });

    return {
      gruposAusentes,
      valores,
    };
  }, [formFichas, fichas, propostaInfo, docsPorFicha]);
  // ── Helpers de aprovação genéricos ─────────────────────────────────────────

  const chamarAprovacao = useCallback(
    (url, body, setProcessando, msgErro) => {
      const tk = localStorage.getItem("token");
      setProcessando(true);
      const ehFormData = body instanceof FormData;
      return fetch(url, {
        method: "POST",
        headers: ehFormData ? { Authorization: `Bearer ${tk}` } : { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: ehFormData ? body : JSON.stringify(body),
      })
        .then(async (r) => {
          const d = await r.json();
          if (!r.ok) throw new Error(d.erro || msgErro);
          setPropostaInfo((prev) => ({ ...prev, ...d }));
          sincronizarAprovacao(d);
          return d;
        })
        .catch((e) => {
          mostrarSnack(e.message || msgErro, "error");
          return null;
        })
        .finally(() => setProcessando(false));
    },
    [sincronizarAprovacao, mostrarSnack],
  );

  // ── Ações de aprovação das FICHAS ──────────────────────────────────────────

  const enviarFichasParaAprovacaoAgora = useCallback(() => {
    const id = propostaInfo?.id;

    if (!id) {
      return;
    }

    setErrosPorFicha({});

    chamarAprovacao(`/api/propostas/${id}/fichas/enviar-aprovacao`, {}, setEnviandoFichas, "Erro ao enviar fichas para aprovação.").then((d) => {
      if (d) {
        setAba("aprovacao");
      }
    });
  }, [propostaInfo, chamarAprovacao]);

  const handleEnviarFichasAprovacao = () => {
    const id = propostaInfo?.id;

    if (!id) {
      return;
    }

    // =========================================================
    // 1. CAMPOS CADASTRAIS OBRIGATÓRIOS
    // =========================================================

    const novosErros = {};

    let primeiroIndiceComErro = -1;

    formFichas.forEach((ficha, index) => {
      const pendencias = pendenciasDaFicha(ficha);

      if (!pendencias.length) {
        return;
      }

      if (primeiroIndiceComErro === -1) {
        primeiroIndiceComErro = index;
      }

      novosErros[index] = pendencias.reduce((acc, pendencia) => {
        acc[pendencia.campo] = `${pendencia.label} é obrigatório.`;

        return acc;
      }, {});
    });

    if (primeiroIndiceComErro !== -1) {
      setErrosPorFicha(novosErros);

      setFichaIdx(primeiroIndiceComErro);

      setAba("dados");

      setSomenteLeitura(false);

      mostrarSnack("Existem campos obrigatórios pendentes. Os campos foram sinalizados na ficha.", "warning");

      setTimeout(() => {
        primeiroCampoRef.current?.focus();
      }, 300);

      return;
    }

    // =========================================================
    // 2. DOCUMENTOS AUSENTES
    // =========================================================

    const { gruposAusentes, valores } = montarAusenciasDocumentais();

    // =========================================================
    // EXISTEM DOCUMENTOS AUSENTES
    // =========================================================

    if (gruposAusentes.length > 0) {
      // Junta os itens somente
      // para realizar a validação.
      const todosItensAusentes = gruposAusentes.flatMap((grupo) => grupo.itens);

      const existeSemJustificativa = todosItensAusentes.some((item) => !String(valores[item.chave] || "").trim());

      // =======================================================
      // EXISTE PELO MENOS UMA JUSTIFICATIVA PENDENTE
      // ABRIR MODAL SEPARADO EM ABAS
      // =======================================================

      if (existeSemJustificativa) {
        setDlgJustificativasDocs({
          aberto: true,

          grupos: gruposAusentes,

          valores,

          abaAtiva: 0,

          salvando: false,
        });

        return;
      }

      // =======================================================
      // TODOS OS DOCUMENTOS AUSENTES JÁ POSSUEM JUSTIFICATIVA
      // =======================================================

      enviarFichasParaAprovacaoAgora();

      return;
    }

    // =========================================================
    // 3. NÃO EXISTE DOCUMENTO AUSENTE
    // =========================================================

    enviarFichasParaAprovacaoAgora();
  };
  const confirmarJustificativasDocumentais = async () => {
    const grupos = dlgJustificativasDocs.grupos;

    const valores = dlgJustificativasDocs.valores;

    // =========================================================
    // TRANSFORMAR OS GRUPOS EM UMA LISTA PARA SALVAR
    // =========================================================

    const itens = grupos.flatMap((grupo) => grupo.itens);

    // =========================================================
    // VALIDAR TODAS AS JUSTIFICATIVAS
    // =========================================================

    const semJustificativa = itens.find((item) => !String(valores[item.chave] || "").trim());

    if (semJustificativa) {
      // =======================================================
      // ABRIR AUTOMATICAMENTE A ABA ONDE ESTÁ O ERRO
      // =======================================================

      const indiceGrupoComErro = grupos.findIndex((grupo) => grupo.itens.some((item) => item.chave === semJustificativa.chave));

      if (indiceGrupoComErro >= 0) {
        setDlgJustificativasDocs((prev) => ({
          ...prev,

          abaAtiva: indiceGrupoComErro,
        }));
      }

      mostrarSnack(`Informe a justificativa para: ${semJustificativa.label}`, "warning");

      return;
    }

    const token = localStorage.getItem("token");

    // =========================================================
    // AGRUPAR JUSTIFICATIVAS POR FICHA
    // =========================================================

    const porFicha = {};

    itens.forEach((item) => {
      if (!porFicha[item.fichaId]) {
        porFicha[item.fichaId] = [];
      }

      porFicha[item.fichaId].push({
        tipoDocumento: item.tipoDocumento,

        justificativa: String(valores[item.chave]).trim(),
      });
    });

    setDlgJustificativasDocs((prev) => ({
      ...prev,

      salvando: true,
    }));

    try {
      // =======================================================
      // SALVAR TODAS AS FICHAS
      // =======================================================

      const respostas = await Promise.all(
        Object.entries(porFicha).map(async ([fichaIdLocal, justificativas]) => {
          const response = await fetch(`/api/ficha-pessoa/${fichaIdLocal}/justificativas-documentos`, {
            method: "PUT",

            headers: {
              "Content-Type": "application/json",

              Authorization: `Bearer ${token}`,
            },

            body: JSON.stringify({
              justificativas,
            }),
          });

          const dados = await response.json().catch(() => ({}));

          if (!response.ok) {
            throw new Error(dados?.erro || "Erro ao salvar justificativas.");
          }

          return {
            fichaId: Number(fichaIdLocal),

            justificativas: Array.isArray(dados?.justificativas) ? dados.justificativas : [],
          };
        }),
      );

      // =======================================================
      // ATUALIZAR AS JUSTIFICATIVAS NO ESTADO LOCAL
      // =======================================================

      setFichas((prev) =>
        prev.map((ficha) => {
          const resposta = respostas.find((itemResposta) => itemResposta.fichaId === ficha.id);

          if (!resposta) {
            return ficha;
          }

          return {
            ...ficha,

            justificativasDocumentos: resposta.justificativas,
          };
        }),
      );

      // =======================================================
      // FECHAR O MODAL
      // =======================================================

      setDlgJustificativasDocs({
        aberto: false,

        grupos: [],

        valores: {},

        abaAtiva: 0,

        salvando: false,
      });

      mostrarSnack("Justificativas registradas. Enviando fichas para aprovação.", "success");

      // =======================================================
      // SOMENTE AGORA ENVIAR PARA APROVAÇÃO
      // =======================================================

      enviarFichasParaAprovacaoAgora();
    } catch (err) {
      console.error("Erro ao salvar justificativas:", err);

      mostrarSnack(err.message || "Erro ao salvar justificativas.", "error");

      setDlgJustificativasDocs((prev) => ({
        ...prev,

        salvando: false,
      }));
    }
  };

  const handleAprovarFichas = () => {
    const id = propostaInfo?.id;
    if (!id) return;
    chamarAprovacao(`/api/propostas/${id}/fichas/aprovar`, {}, setProcessandoFichas, "Erro ao aprovar fichas.").then(
      (r) => r && mostrarSnack("Fichas aprovadas com sucesso!"),
    );
  };

  const handleReprovarFichas = () => {
    const id = propostaInfo?.id;
    if (!id || !dlgReprovarFichas.motivo.trim()) return;
    chamarAprovacao(`/api/propostas/${id}/fichas/reprovar`, { motivo: dlgReprovarFichas.motivo }, setProcessandoFichas, "Erro ao reprovar fichas.").then(
      (r) => {
        if (r) {
          setDlgReprovarFichas({ aberto: false, motivo: "" });
          mostrarSnack("Fichas reprovadas.", "warning");
        }
      },
    );
  };

  const handleQuestionarFichas = () => {
    const id = propostaInfo?.id;
    if (!id || !dlgQuestionarFichas.motivo.trim() || !dlgQuestionarFichas.idsUsuarioResposta.length) return;
    const fd = new FormData();
    fd.append("motivo", dlgQuestionarFichas.motivo);
    fd.append("idsUsuarioResposta", JSON.stringify(dlgQuestionarFichas.idsUsuarioResposta));
    if (dlgQuestionarFichas.arquivo) fd.append("arquivo", dlgQuestionarFichas.arquivo);
    chamarAprovacao(`/api/propostas/${id}/fichas/questionar`, fd, setProcessandoFichas, "Erro ao questionar.").then((r) => {
      if (r) {
        setDlgQuestionarFichas({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null });
        mostrarSnack("Questão enviada.");
      }
    });
  };

  const handleResponderFichas = () => {
    const id = propostaInfo?.id;
    if (!id || !dlgResponderFichas.resposta.trim()) return;
    const fd = new FormData();
    fd.append("resposta", dlgResponderFichas.resposta);
    if (dlgResponderFichas.arquivo) fd.append("arquivo", dlgResponderFichas.arquivo);
    chamarAprovacao(`/api/propostas/${id}/fichas/responder`, fd, setProcessandoFichas, "Erro ao responder.").then((r) => {
      if (r) {
        setDlgResponderFichas({ aberto: false, resposta: "", pergunta: "", arquivo: null });
        mostrarSnack("Resposta enviada.");
      }
    });
  };

  const handleRevisarFichas = () => {
    const id = propostaInfo?.id;
    if (!id || !dlgRevisarFichas.motivo.trim()) return;
    chamarAprovacao(`/api/propostas/${id}/fichas/revisar`, { motivo: dlgRevisarFichas.motivo }, setProcessandoFichas, "Erro ao revisar fichas.").then((r) => {
      if (r) {
        setDlgRevisarFichas({ aberto: false, motivo: "" });
        setAba("dados");
        mostrarSnack("Fichas devolvidas para preenchimento.");
      }
    });
  };

  // ── Upload de análise de crédito ───────────────────────────────────────────

  const handleUploadAnalise = () => {
    const id = propostaInfo?.id;

    if (!id || !arquivoAnalise) {
      return mostrarSnack("Selecione um arquivo.", "warning");
    }

    const tk = localStorage.getItem("token");

    setUploadandoAnalise(true);

    const fd = new FormData();

    fd.append("arquivo", arquivoAnalise);

    fd.append("nomeArquivo", nomeAnalise.trim() || arquivoAnalise.name);

    fd.append("observacaoAnaliseCredito", observacaoAnaliseCredito.trim());

    fetch(`/api/propostas/${id}/analise-credito/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${tk}`,
      },
      body: fd,
    })
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.erro || "Erro ao enviar.");
        setArquivoAnalise(null);
        setNomeAnalise("");
        recarregarProposta(id).then((p) => mostrarSnack("Documento enviado com sucesso!"));
      })
      .catch((e) => mostrarSnack(e.message || "Erro ao enviar arquivo.", "error"))
      .finally(() => setUploadandoAnalise(false));
  };

  const handleDeleteAnaliseDoc = (docId) => {
    const id = propostaInfo?.id;
    if (!id) return;
    const tk = localStorage.getItem("token");
    fetch(`/api/propostas/${id}/analise-credito/docs/${docId}`, { method: "DELETE", headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => {
        if (r.ok) recarregarProposta(id);
        else mostrarSnack("Erro ao excluir.", "error");
      })
      .catch(() => mostrarSnack("Erro ao excluir.", "error"));
  };

  const handleDownloadAnaliseDoc = (docId, nomeOriginal) => {
    const id = propostaInfo?.id;
    if (!id) return;
    const tk = localStorage.getItem("token");
    fetch(`/api/propostas/${id}/analise-credito/docs/${docId}/download`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject("Erro ao baixar.")))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = nomeOriginal;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => mostrarSnack("Erro ao baixar arquivo.", "error"));
  };

  const handleVisualizarAnaliseDoc = (docId) => {
    const id = propostaInfo?.id;
    if (!id) return;
    const tk = localStorage.getItem("token");
    fetch(`/api/propostas/${id}/analise-credito/docs/${docId}/download`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject("Erro ao visualizar.")))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => mostrarSnack("Erro ao visualizar arquivo.", "error"));
  };

  // ── Ações de aprovação da ANÁLISE DE CRÉDITO ──────────────────────────────

  const handleEnviarAnaliseAprovacao = () => {
    const id = propostaInfo?.id;

    if (!id) {
      return;
    }

    chamarAprovacao(
      `/api/propostas/${id}/analise-credito/enviar-aprovacao`,
      {
        observacaoAnaliseCredito: observacaoAnaliseCredito.trim(),
      },
      setEnviandoAnalise,
      "Erro ao enviar análise para aprovação.",
    ).then((r) => r && mostrarSnack("Análise de crédito enviada para aprovação!"));
  };

  const handleAprovarAnalise = () => {
    const id = propostaInfo?.id;
    if (!id) return;
    chamarAprovacao(`/api/propostas/${id}/analise-credito/aprovar`, {}, setProcessandoAnalise, "Erro ao aprovar análise.").then(
      (r) => r && mostrarSnack("Análise de crédito aprovada!"),
    );
  };

  const handleReprovarAnalise = () => {
    const id = propostaInfo?.id;
    if (!id || !dlgReprovarAnalise.motivo.trim()) return;
    chamarAprovacao(
      `/api/propostas/${id}/analise-credito/reprovar`,
      { motivo: dlgReprovarAnalise.motivo },
      setProcessandoAnalise,
      "Erro ao reprovar análise.",
    ).then((r) => {
      if (r) {
        setDlgReprovarAnalise({ aberto: false, motivo: "" });
        mostrarSnack("Análise de crédito reprovada.", "warning");
      }
    });
  };

  const handleQuestionarAnalise = () => {
    const id = propostaInfo?.id;
    if (!id || !dlgQuestionarAnalise.motivo.trim() || !dlgQuestionarAnalise.idsUsuarioResposta.length) return;
    const fd = new FormData();
    fd.append("motivo", dlgQuestionarAnalise.motivo);
    fd.append("idsUsuarioResposta", JSON.stringify(dlgQuestionarAnalise.idsUsuarioResposta));
    if (dlgQuestionarAnalise.arquivo) fd.append("arquivo", dlgQuestionarAnalise.arquivo);
    chamarAprovacao(`/api/propostas/${id}/analise-credito/questionar`, fd, setProcessandoAnalise, "Erro ao questionar.").then((r) => {
      if (r) {
        setDlgQuestionarAnalise({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null });
        mostrarSnack("Questão enviada.");
      }
    });
  };

  const handleResponderAnalise = () => {
    const id = propostaInfo?.id;
    if (!id || !dlgResponderAnalise.resposta.trim()) return;
    const fd = new FormData();
    fd.append("resposta", dlgResponderAnalise.resposta);
    if (dlgResponderAnalise.arquivo) fd.append("arquivo", dlgResponderAnalise.arquivo);
    chamarAprovacao(`/api/propostas/${id}/analise-credito/responder`, fd, setProcessandoAnalise, "Erro ao responder.").then((r) => {
      if (r) {
        setDlgResponderAnalise({ aberto: false, resposta: "", pergunta: "", arquivo: null });
        mostrarSnack("Resposta enviada.");
      }
    });
  };

  const handleRevisarAnalise = () => {
    const id = propostaInfo?.id;
    if (!id || !dlgRevisarAnalise.motivo.trim()) return;
    chamarAprovacao(
      `/api/propostas/${id}/analise-credito/revisar`,
      { motivo: dlgRevisarAnalise.motivo },
      setProcessandoAnalise,
      "Erro ao revisar análise de crédito.",
    ).then((r) => {
      if (r) {
        setDlgRevisarAnalise({ aberto: false, motivo: "" });
        mostrarSnack("Análise de crédito devolvida para novo upload.");
      }
    });
  };

  // ── Sub-componente: painel de aprovação (reutilizado para fichas e análise) ──

  // ============================================================
  // JUSTIFICATIVAS DOCUMENTAIS PARA VISUALIZAÇÃO DO APROVADOR
  // ============================================================

  const gruposJustificativasAprovacao = useMemo(() => {
    const resultado = [];

    formFichas.forEach((fichaForm, index) => {
      const fichaBanco = fichas[index];

      if (!fichaBanco?.id) {
        return;
      }

      const justificativas = Array.isArray(fichaBanco?.justificativasDocumentos) ? fichaBanco.justificativasDocumentos : [];

      if (justificativas.length === 0) {
        return;
      }

      const gruposChecklist = obterGruposChecklistDocumentosFicha(fichaForm, propostaInfo);

      const nomeFicha = fichaForm.tipoPessoa === "PJ" ? fichaForm.razaoSocial || "Pessoa Jurídica" : fichaForm.pfNome || "Pessoa Física";

      const papel = LABEL_CAMPO[fichaForm.campoProposta] || fichaForm.campoProposta;

      gruposChecklist.forEach((grupoChecklist) => {
        const itensGrupo = grupoChecklist.itens
          .map((itemChecklist) => {
            const justificativa = justificativas.find((justificativaBanco) => String(justificativaBanco.tipoDocumento) === String(itemChecklist.tipo));

            if (!justificativa) {
              return null;
            }

            return {
              id: justificativa.id,

              tipoDocumento: justificativa.tipoDocumento,

              label: itemChecklist.label,

              justificativa: justificativa.justificativa,
            };
          })
          .filter(Boolean);

        if (itensGrupo.length === 0) {
          return;
        }

        resultado.push({
          chave: `${fichaBanco.id}::${grupoChecklist.chave}`,

          titulo: grupoChecklist.titulo,

          fichaNome: `${papel} ${fichaForm.tipoPessoa} - ${nomeFicha}`,

          itens: itensGrupo,
        });
      });
    });

    return resultado;
  }, [formFichas, fichas, propostaInfo]);

  const fichaAtual = formFichas[fichaIdx] ?? null;

  const fichaId = fichas[fichaIdx]?.id ?? null;

  const garantiaNormalizada = String(propostaInfo?.garantia || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

  const ehFiador = fichaAtual?.campoProposta === "Fiador";

  const ehLocatario = fichaAtual?.campoProposta === "Locataria";

  const ehPessoaJuridica = fichaAtual?.tipoPessoa === "PJ";

  const ehPessoaFisica = fichaAtual?.tipoPessoa === "PF";

  const garantiaImobiliaria = garantiaNormalizada
    .split(",")
    .map((g) => g.trim())
    .includes("IMOBILIARIA");

  let checklistAtual = [];

  if (ehFiador && ehPessoaJuridica) {
    checklistAtual = CHECKLIST_FIADOR_PJ;
  } else if (ehFiador && ehPessoaFisica) {
    checklistAtual = CHECKLIST_FIADOR_PF;
  } else if (ehLocatario && ehPessoaFisica && garantiaImobiliaria) {
    checklistAtual = CHECKLIST_LOCATARIO_PF_IMOBILIARIA;
  } else if (ehLocatario && ehPessoaJuridica) {
    checklistAtual = CHECKLIST_LOCATARIO_PJ_PADRAO;
  } else if (ehLocatario && ehPessoaFisica) {
    checklistAtual = CHECKLIST_LOCATARIO_PF_PADRAO;
  }

  // Matrícula/IPTU: rótulo dinâmico PARA FINS / NÃO É PARA FINS DE GARANTIA
  checklistAtual = aplicarFinsGarantia(checklistAtual, fichaEhParaFinsDeGarantia(fichaAtual, propostaInfo));

  const fiadorComImovel =
    String(propostaInfo?.fiadorTipoGarantia || "")
      .trim()
      .toUpperCase() === "COM_IMOVEL";

  const mostrarLinkGoogleChecklist = (ehFiador && fiadorComImovel) || (ehLocatario && garantiaImobiliaria);

  const linkGoogleImovel = fichaAtual?.linkGoogleImovel || "";

  const fichasBloqueadas = ["EmAprovacao", "Aprovada", "Reprovada", "Inutilizada"].includes(propostaInfo?.statusFichas);
  const propostaNaoAprovada = propostaInfo?.statusGeral !== "Aprovada";
  const ro = somenteLeitura || !podeCadastrar || fichasBloqueadas || propostaNaoAprovada;

  // ── Erros de campo obrigatório  ────────────────
  const errosForm = errosPorFicha[fichaIdx] ?? EMPTY_ERROS_FORM;
  const setErrosForm = useCallback(
    (updater) =>
      setErrosPorFicha((prev) => ({
        ...prev,
        [fichaIdx]: typeof updater === "function" ? updater(prev[fichaIdx] ?? {}) : updater,
      })),
    [fichaIdx],
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        {/* Cabeçalho */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          {/* ======================================================= */}
          {/* LADO ESQUERDO                                          */}
          {/* ======================================================= */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            {propostaInfo && (
              <Tooltip title="Voltar">
                <IconButton
                  onClick={handleVoltarParaLista}
                  sx={{
                    color: "var(--ig-t-aaaaaa)",

                    "&:hover": {
                      color: "var(--ig-t-ffffff)",
                    },

                    width: "auto",
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            )}

            <Typography
              variant="h5"
              sx={{
                color: "var(--ig-t-ffffff)",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Ficha de Cadastro
            </Typography>

            {propostaInfo && (
              <Chip
                label={`Proposta ${propostaInfo.codigo ?? `#${propostaInfo.id}`}`}
                sx={{
                  ml: 1,
                  backgroundColor: "#1998a8",
                  color: "#fff",
                  fontWeight: 600,
                }}
              />
            )}
          </Box>

          {/* ======================================================= */}
          {/* TEMPLATE FC                                            */}
          {/* ======================================================= */}

          <Button
            type="button"
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleTemplateFC}
            sx={{
              width: "auto",
              minWidth: 145,
              height: 40,

              backgroundColor: "#e65100",
              color: "#fff",

              fontWeight: 700,
              textTransform: "none",
              whiteSpace: "nowrap",

              "&:hover": {
                backgroundColor: "#bf360c",
              },
            }}
          >
            TEMPLATE FC
          </Button>
        </Box>

        {/* ── Grid de propostas/fichas — sempre montado, colapsa quando um form abre ── */}
        <Box sx={{ mb: 3 }}>
          {filtroDashboard && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={
                  filtroDashboard.tipo === "statusAprovacao"
                    ? `Filtrado por: ${LABEL_STATUS_APROVACAO[filtroDashboard.valor] ?? filtroDashboard.valor} — ${LABEL_PROCESSO_DASHBOARD[filtroDashboard.processo]}${filtroDashboard.geral ? " (todos os usuários)" : ""}`
                    : filtroDashboard.tipo === "minhasPropostas"
                      ? `Filtrado por: Minhas ${filtroDashboard.comAnaliseCredito ? "Análises de Crédito" : "Fichas"}`
                      : filtroDashboard.tipo === "comAnaliseCredito"
                        ? "Filtrado por: Análises de Crédito Cadastradas"
                        : "Filtrado por: Fichas Cadastradas"
                }
                onDelete={handleLimparFiltroDashboard}
                sx={{ backgroundColor: "#1998a8", color: "#fff", fontWeight: "bold" }}
              />
            </Box>
          )}

          {/* Filtro + Copiar + CSV */}
          <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Filtrar Fichas de Cadastro — combine termos com %"
              value={inputFiltro}
              onChange={(e) => setInputFiltro(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handlePesquisar();
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "transparent",
                  color: "var(--ig-t-ffffff)",
                },
                "& input::placeholder": {
                  color: "var(--ig-t-ffffff)",
                  opacity: 1,
                  fontWeight: 500,
                  fontSize: "1rem",
                },
              }}
            />

            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={handleLimparFiltro}
              sx={{
                flexShrink: 0,
                width: "auto",
                height: 40,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCopiar}
              sx={{
                height: 40,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i
                className="fas fa-copy"
                style={{
                  marginRight: 6,
                }}
              />
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 40,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            {!gridMinimizado ? (
              <button
                type="button"
                onClick={() => setGridMinimizado(true)}
                style={{
                  border: "1px solid #d32f2f",
                  color: "#ff4c4c",
                  background: "transparent",
                  padding: "5px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                  width: "fit-content",
                  display: "inline-block",
                }}
              >
                ↘ Minimizar
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setGridMinimizado(false)}
                style={{
                  border: "1px solid #1998a8",
                  color: "#1998a8",
                  background: "transparent",
                  padding: "5px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                  width: "fit-content",
                  display: "inline-block",
                }}
              >
                ↗ Expandir
              </button>
            )}
          </Box>

          {/* Grid de propostas/fichas */}
          <GradeDePropostas
            gridMinimizado={gridMinimizado}
            carregandoPropostas={carregandoPropostas}
            propostas={propostas}
            totalPropostas={totalPropostas}
            paginaLista={paginaLista}
            porPaginaLista={porPaginaLista}
            propostaSelecionadaId={propostaInfo?.id}
            onVisualizar={handleVisualizarProposta}
            onImprimir={handleImprimirFichas}
            setPaginaLista={setPaginaLista}
            setPorPaginaLista={setPorPaginaLista}
          />
        </Box>

        {/* ── Loading fichas ───────────────────────────────────────────────── */}
        {carregandoFichas && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#1998a8" }} />
          </Box>
        )}

        {/* ── Proposta + fichas carregadas ─────────────────────────────────── */}
        {propostaInfo && !carregandoFichas && (
          <>
            {/* Banner da proposta */}
            <Paper sx={{ p: 2, mb: 2, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, border: "1px solid var(--ig-b-333333)" }}>
              <Typography variant="h6" sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
                {somenteLeitura ? "Visualizar Ficha de Cadastro" : "Editar Ficha de Cadastro"}
              </Typography>
              <Typography variant="body2" sx={{ color: "var(--ig-t-aaaaaa)", fontWeight: 500, mb: 1.5 }}>
                Proposta {propostaInfo.codigo ?? `#${propostaInfo.id}`}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
                    {propostaInfo.imovel?.apelido && (
                      <Typography component="span" sx={{ color: "#1998a8", fontWeight: 700, mr: 0.5 }}>
                        {propostaInfo.imovel.apelido}
                      </Typography>
                    )}
                    {propostaInfo.enderecoImovel || "—"}
                  </Typography>
                  <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.82rem" }}>
                    {propostaInfo.tipoLocacao} · Garantia: {propostaInfo.garantia || "—"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75 }}>
                    <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.72rem" }}>Status da Proposta:</Typography>
                    {chipStatusGeral(propostaInfo.statusGeral)}
                  </Box>
                  {propostaInfo.statusFichas && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75, mt: 0.5 }}>
                      <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.72rem" }}>Status da Ficha:</Typography>
                      {chipStatusFichas(propostaInfo.statusFichas)}
                    </Box>
                  )}
                  {propostaInfo.statusAnaliseCredito && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75, mt: 0.5 }}>
                      <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.72rem" }}>Status da Análise de Crédito:</Typography>
                      {chipStatusAnaliseCredito(propostaInfo.statusAnaliseCredito)}
                    </Box>
                  )}
                  <Typography sx={{ color: "var(--ig-t-666666)", fontSize: "0.78rem", mt: 0.5 }}>
                    {fichas.length} ficha{fichas.length !== 1 ? "s" : ""}
                  </Typography>
                </Box>
              </Box>

              {/* Identificação — dados básicos da(s) Locatária(s), informados na Proposta Comercial */}
              {fichas.filter((f) => f.campoProposta === "Locataria").length > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid var(--ig-b-333333)", display: "flex", flexDirection: "column", gap: 1 }}>
                  {fichas
                    .filter((f) => f.campoProposta === "Locataria")
                    .map((loc, i, arr) => (
                      <Box key={loc.id}>
                        <Typography sx={{ color: "var(--ig-t-666666)", fontSize: "0.7rem", fontWeight: 700, letterSpacing: 0.5, mb: 0.5 }}>
                          {arr.length > 1 ? `LOCATÁRIA ${i + 1}` : "LOCATÁRIA"}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                          <Box>
                            <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.72rem" }}>Tipo de Pessoa</Typography>
                            <Typography sx={{ color: "var(--ig-t-ffffff)", fontSize: "0.85rem", fontWeight: 600 }}>
                              {loc.tipoPessoa === "PJ" ? "Pessoa Jurídica" : "Pessoa Física"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.72rem" }}>{loc.tipoPessoa === "PJ" ? "CNPJ" : "CPF"}</Typography>
                            <Typography sx={{ color: "var(--ig-t-ffffff)", fontSize: "0.85rem", fontWeight: 600 }}>
                              {(loc.tipoPessoa === "PJ" ? loc.cnpj : loc.cpf) || descreverDocumentoEstrangeiro(loc) || "—"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.72rem" }}>Email informado na PC</Typography>
                            <Typography sx={{ color: "var(--ig-t-ffffff)", fontSize: "0.85rem", fontWeight: 600 }}>{loc.email || "—"}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                </Box>
              )}

              {(aba === "dados" || aba === "aprovacao") && propostaInfo.statusFichas === "EmPreenchimento" && podeCadastrar && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleEnviarFichasAprovacao}
                    disabled={enviandoFichas}
                    sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
                  >
                    {enviandoFichas ? "Enviando..." : "Enviar Fichas para Aprovação"}
                  </Button>
                </Box>
              )}
              {aba === "analise" && propostaInfo.statusAnaliseCredito === "AguardandoUpload" && docsAnalise.length > 0 && podeCadastrar && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleEnviarAnaliseAprovacao}
                    disabled={enviandoAnalise}
                    sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
                  >
                    {enviandoAnalise ? "Enviando..." : "Enviar Análise para Aprovação"}
                  </Button>
                </Box>
              )}
            </Paper>

            {/* ── Ficha de Cadastro: abas (mesmo padrão visual da Proposta Comercial) ─────── */}
            <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
              <Tabs
                value={aba}
                onChange={(_, v) => setAba(v)}
                sx={{
                  mb: 2,
                  borderBottom: "1px solid var(--ig-b-333333)",
                  "& .MuiTab-root": { color: "var(--ig-t-aaaaaa)", textTransform: "none", fontWeight: 600 },
                  "& .MuiTab-root.Mui-selected": { color: "#1998a8" },
                  "& .MuiTabs-indicator": { backgroundColor: "#1998a8" },
                }}
              >
                <Tab label="Dados das Fichas" value="dados" />
                <Tab label="Aprovação das Fichas" value="aprovacao" />
                {propostaInfo?.statusAnaliseCredito && <Tab label="Análise de Crédito" value="analise" />}
                {propostaInfo?.statusAnaliseCredito && <Tab label="Aprovação da Análise de Crédito" value="aprovacaoAnalise" />}
              </Tabs>

              {aba === "aprovacao" && (
                <>
                  {/* ===================================================== */}
                  {/* JUSTIFICATIVAS DOS DOCUMENTOS AUSENTES               */}
                  {/* ===================================================== */}

                  {gruposJustificativasAprovacao.length > 0 && (
                    <Paper
                      sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        borderRadius: 2,
                        border: "1px solid var(--ig-b-333333)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "var(--ig-t-ffffff)",
                          fontWeight: 700,
                          fontSize: "1rem",
                          mb: 0.5,
                        }}
                      >
                        Justificativas dos Documentos Ausentes
                      </Typography>

                      <Typography
                        sx={{
                          color: "var(--ig-t-aaaaaa)",
                          fontSize: "0.82rem",
                          mb: 2,
                        }}
                      >
                        Os documentos abaixo não foram anexados. Analise as justificativas apresentadas antes de aprovar, questionar ou reprovar as fichas.
                      </Typography>

                      {/* ================================================= */}
                      {/* ABAS - UMA POR CHECKLIST                          */}
                      {/* ================================================= */}

                      <Tabs
                        value={Math.min(abaJustificativaAprovacao, Math.max(gruposJustificativasAprovacao.length - 1, 0))}
                        onChange={(_, novaAba) => setAbaJustificativaAprovacao(novaAba)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          mb: 2,
                          borderBottom: "1px solid var(--ig-b-444444)",

                          "& .MuiTab-root": {
                            color: "var(--ig-t-aaaaaa)",
                            fontWeight: 700,
                            textTransform: "none",
                            minHeight: 48,
                          },

                          "& .MuiTab-root.Mui-selected": {
                            color: "#f97316",
                          },

                          "& .MuiTabs-indicator": {
                            backgroundColor: "#f97316",
                          },

                          "& .MuiTabScrollButton-root": {
                            color: "var(--ig-t-ffffff)",
                          },
                        }}
                      >
                        {gruposJustificativasAprovacao.map((grupo) => (
                          <Tab key={grupo.chave} label={`${grupo.titulo} - ${grupo.fichaNome}`} />
                        ))}
                      </Tabs>

                      {/* ================================================= */}
                      {/* CONTEÚDO DA ABA ATIVA                            */}
                      {/* ================================================= */}

                      {gruposJustificativasAprovacao.map((grupo, indiceGrupo) => {
                        const indiceAtual = Math.min(abaJustificativaAprovacao, Math.max(gruposJustificativasAprovacao.length - 1, 0));

                        if (indiceGrupo !== indiceAtual) {
                          return null;
                        }

                        return (
                          <Box key={grupo.chave}>
                            <Box
                              sx={{
                                p: 1.5,
                                mb: 2,
                                backgroundColor: "var(--ig-f-252525)",
                                border: "1px solid var(--ig-b-3a3a3a)",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#f97316",
                                  fontWeight: 700,
                                  fontSize: "0.88rem",
                                }}
                              >
                                {grupo.fichaNome}
                              </Typography>

                              <Typography
                                sx={{
                                  color: "var(--ig-t-aaaaaa)",
                                  fontSize: "0.76rem",
                                  mt: 0.4,
                                }}
                              >
                                Checklist: {grupo.titulo}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              {grupo.itens.map((item) => (
                                <Box
                                  key={item.id ?? item.tipoDocumento}
                                  sx={{
                                    p: 1.5,
                                    backgroundColor: "var(--ig-f-252525)",
                                    border: "1px solid var(--ig-b-3a3a3a)",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: "var(--ig-t-ffffff)",
                                      fontWeight: 600,
                                      fontSize: "0.86rem",
                                      mb: 1,
                                    }}
                                  >
                                    {item.label}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: "var(--ig-t-888888)",
                                      fontWeight: 700,
                                      fontSize: "0.7rem",
                                      mb: 0.5,
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    Justificativa da ausência
                                  </Typography>

                                  <Box
                                    sx={{
                                      p: 1.2,
                                      backgroundColor: "var(--ig-f-2a2a2a)",
                                      border: "1px solid var(--ig-b-444444)",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: "var(--ig-t-ffffff)",
                                        fontSize: "0.86rem",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {item.justificativa}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Paper>
                  )}

                  {/* ===================================================== */}
                  {/* PAINEL NORMAL DE APROVAÇÃO                           */}
                  {/* ===================================================== */}

                  <PainelAprovacao
                    titulo="Aprovação das Fichas de Cadastro"
                    statusField={propostaInfo.statusFichas}
                    mostrarCabecalho={false}
                    aprovacao={aprovacaoFichas}
                    autorizacoes={autorizacoesFichas}
                    historicos={historicosFichas}
                    idUsuarioLogado={idUsuarioLogado}
                    idCriador={propostaInfo.idUsuarioCriacao ?? null}
                    revisarPorAlcada
                    usuarioEmAlcada={propostaInfo.usuarioEhAprovador}
                    usuarioEhAprovador={propostaInfo.usuarioEhAprovador}
                    onEnviar={handleEnviarFichasAprovacao}
                    enviando={enviandoFichas}
                    onAprovar={handleAprovarFichas}
                    onAbrirReprovar={() => setDlgReprovarFichas({ aberto: true, motivo: "" })}
                    onAbrirQuestionar={() => setDlgQuestionarFichas({ aberto: true, motivo: "", idsUsuarioResposta: [], arquivo: null })}
                    onAbrirResponder={(pergunta) => setDlgResponderFichas({ aberto: true, resposta: "", pergunta, arquivo: null })}
                    onConfirmarRevisar={() => {
                      document.activeElement?.blur();
                      setDlgRevisarFichas({ aberto: true, motivo: "" });
                    }}
                    mostrarBotaoEnviar={false}
                    processando={processandoFichas}
                    podeEditar={podeCadastrar}
                    rotaAnexoBase="/api/propostas"
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleVoltarParaLista}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",

                        "&:hover": {
                          backgroundColor: "var(--ig-f-255-255-255-a005)",
                        },

                        fontWeight: "bold",

                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                  </Box>
                </>
              )}

              {aba === "dados" &&
                (fichas.length === 0 ? (
                  <Paper sx={{ p: 4, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, textAlign: "center" }}>
                    <Typography sx={{ color: "var(--ig-t-666666)", fontStyle: "italic" }}>Nenhuma ficha encontrada para esta proposta.</Typography>
                  </Paper>
                ) : (
                  <Paper sx={{ backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, border: "1px solid var(--ig-b-333333)" }}>
                    {/* Tabs das fichas — padrão visual da tela Relógio de Água (abas em formato de pasta) */}
                    <Tabs
                      value={fichaIdx}
                      onChange={(_, v) => {
                        setFichaIdx(v);
                        focarPrimeiroCampo();
                      }}
                      variant="scrollable"
                      scrollButtons="auto"
                      TabIndicatorProps={{ sx: { display: "none" } }}
                      sx={{
                        minHeight: 0,
                        borderBottom: "1px solid var(--ig-b-334155)",
                        "& .MuiTabs-flexContainer": { gap: "10px", p: "8px 8px 0" },
                        "& .MuiTab-root": {
                          minHeight: 46,
                          minWidth: 150,
                          padding: "10px 18px",
                          backgroundColor: "var(--ig-f-1c2a3a)",
                          color: "var(--ig-t-ffffff)",
                          borderRadius: "6px 6px 0 0",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.82rem",
                          opacity: 0.6,
                          transition: "0.2s",
                          "&:hover": { opacity: 1 },
                        },
                        "& .MuiTab-root.Mui-selected": {
                          backgroundColor: "#0d6efd",
                          opacity: 1,
                          boxShadow: "0 4px 10px rgba(13,110,253,.4)",
                        },
                        "& .MuiTabScrollButton-root": { color: "var(--ig-t-aaaaaa)", width: "auto" },
                      }}
                    >
                      {fichas.map((f, i) => (
                        <Tab key={f.id} value={i} label={labelFicha(f)} />
                      ))}
                    </Tabs>

                    {/* Link público permitido exclusivamente para Locatária e Fiador. */}
                    {fichaAtual && podeCadastrar && permiteFichaCadastral(fichaAtual.campoProposta) && !fichasBloqueadas && !propostaNaoAprovada && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", px: { xs: 2, sm: 3 }, pt: 2 }}>
                        <Button
                          size="small"
                          startIcon={<FaCopy />}
                          onClick={() => copiarLinkPublicoFicha(fichas[fichaIdx])}
                          sx={{
                            width: "auto",
                            backgroundColor: "#1998a8",
                            color: "#fff",
                            "&:hover": { backgroundColor: "#1565c0" },
                            textTransform: "none",
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Copiar link de preenchimento
                        </Button>
                        <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.8rem" }}>
                          Envie este link para a pessoa preencher a própria ficha, sem login. Ela precisará dos 5 primeiros caracteres do
                          {fichaAtual.estrangeiro && !(fichaAtual.tipoPessoa === "PJ" ? fichaAtual.cnpj : fichaAtual.cpf)
                            ? " documento estrangeiro"
                            : fichaAtual.tipoPessoa === "PJ"
                              ? " CNPJ"
                              : " CPF"}{" "}
                          desta ficha.
                        </Typography>
                      </Box>
                    )}

                    {/* Form da ficha ativa */}
                    {fichaAtual && (
                      <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <FichaPessoaForm
                          fichaAtual={fichaAtual}
                          fichaId={fichaId}
                          ro={ro}
                          ehAprovador={ehAprovador}
                          setField={setField}
                          errosForm={errosForm}
                          setErrosForm={setErrosForm}
                          buscandoCnpj={buscandoCnpj}
                          handleCnpjChange={handleCnpjChange}
                          primeiroCampoRef={primeiroCampoRef}
                          checklistAtual={checklistAtual}
                          docsPorFicha={docsPorFicha}
                          uploadState={uploadState}
                          uploadando={uploadando}
                          mostrarLinkGoogleChecklist={mostrarLinkGoogleChecklist}
                          linkGoogleImovel={linkGoogleImovel}
                          setUploadField={setUploadField}
                          handleUploadDoc={handleUploadDoc}
                          handleDeleteDoc={handleDeleteDoc}
                          handleVisualizarDoc={handleVisualizarDoc}
                          handleDownloadDoc={handleDownloadDoc}
                        />

                        {/* Rodapé */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, pt: 2, borderTop: "1px solid var(--ig-b-333333)" }}>
                          {somenteLeitura && (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                onClick={handleVoltarParaLista}
                                sx={{
                                  width: "auto",
                                  borderColor: "var(--ig-b-555555)",
                                  color: "var(--ig-t-cccccc)",
                                  "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                                  fontWeight: "bold",
                                  px: 3,
                                }}
                              >
                                Fechar
                              </Button>
                              {podeCadastrar && !fichasBloqueadas && !propostaNaoAprovada && (
                                <Button
                                  variant="contained"
                                  startIcon={<FaEdit />}
                                  onClick={() => {
                                    setSomenteLeitura(false);
                                    focarPrimeiroCampo();
                                  }}
                                  sx={{
                                    width: "auto",
                                    backgroundColor: "#1998a8",
                                    "&:hover": { backgroundColor: "#1565c0" },
                                    fontWeight: "bold",
                                    py: 1,
                                    px: 4,
                                  }}
                                >
                                  Editar
                                </Button>
                              )}
                            </Box>
                          )}
                          {podeCadastrar && !fichasBloqueadas && !propostaNaoAprovada && !somenteLeitura && (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                startIcon={<FaTimes />}
                                onClick={handleCancelarEdicaoFicha}
                                disabled={salvando}
                                sx={{
                                  width: "auto",
                                  borderColor: "var(--ig-b-555555)",
                                  color: "var(--ig-t-cccccc)",
                                  "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                                  fontWeight: "bold",
                                  px: 3,
                                }}
                              >
                                Cancelar
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={salvando ? <CircularProgress size={16} sx={{ color: "var(--ig-t-ffffff)" }} /> : <FaSave />}
                                onClick={handleSalvar}
                                disabled={salvando}
                                sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                              >
                                {salvando ? "Salvando..." : "Atualizar"}
                              </Button>
                            </Box>
                          )}
                          {propostaNaoAprovada && (
                            <Typography sx={{ color: "var(--ig-t-666666)", fontSize: "0.8rem", fontStyle: "italic" }}>
                              Proposta ainda não aprovada — ficha só pode ser preenchida após a aprovação.
                            </Typography>
                          )}
                          {!propostaNaoAprovada && fichasBloqueadas && (
                            <Typography sx={{ color: "var(--ig-t-666666)", fontSize: "0.8rem", fontStyle: "italic" }}>
                              {propostaInfo?.statusFichas === "Aprovada" ? "Fichas aprovadas — edição encerrada." : "Fichas em aprovação — edição bloqueada."}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ))}

              {/* ── Análise de Crédito ────────────────────────────────────────────── */}
              {aba === "analise" && propostaInfo?.statusAnaliseCredito && (
                <Box>
                  {/* Upload de documento */}
                  {propostaInfo.statusAnaliseCredito === "AguardandoUpload" && podeCadastrar && (
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, border: "1px solid var(--ig-b-333333)" }}>
                      <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.82rem", mb: 1.5 }}>
                        Envie o documento de análise de crédito da locatária:
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          component="label"
                          size="small"
                          startIcon={<FaPaperclip />}
                          sx={{
                            backgroundColor: "#007bff",
                            color: "#fff",
                            fontSize: "0.74rem",
                            fontWeight: 700,
                            "&:hover": { backgroundColor: "#0069d9" },
                            textTransform: "none",
                            px: 1.5,
                            width: "auto",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={arquivoAnalise ? arquivoAnalise.name : ""}
                        >
                          {arquivoAnalise ? arquivoAnalise.name : "GED"}
                          <input type="file" hidden onChange={(e) => setArquivoAnalise(e.target.files[0] ?? null)} />
                        </Button>
                        {arquivoAnalise && (
                          <TextField
                            size="small"
                            placeholder="Nome descritivo (opcional)"
                            value={nomeAnalise}
                            onChange={(e) => setNomeAnalise(e.target.value.toUpperCase())}
                            sx={{
                              flex: 1,
                              minWidth: 130,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "var(--ig-f-2a2a2a)",
                                color: "var(--ig-t-ffffff)",
                                "& fieldset": { borderColor: "var(--ig-b-444444)" },
                                "&:hover fieldset": { borderColor: "var(--ig-b-888888)" },
                              },
                              "& input": { color: "var(--ig-t-ffffff)", fontSize: "0.79rem", py: "5px", textTransform: "uppercase" },
                            }}
                          />
                        )}
                        {arquivoAnalise && (
                          <Button
                            size="small"
                            onClick={handleUploadAnalise}
                            disabled={uploadandoAnalise}
                            startIcon={uploadandoAnalise ? <CircularProgress size={12} sx={{ color: "var(--ig-t-ffffff)" }} /> : null}
                            sx={{
                              backgroundColor: "#1998a8",
                              color: "#fff",
                              fontSize: "0.74rem",
                              "&:hover": { backgroundColor: "#1565c0" },
                              "&.Mui-disabled": { backgroundColor: "var(--ig-f-333333)", color: "#555" },
                              textTransform: "none",
                              px: 1.5,
                              width: "auto",
                            }}
                          >
                            {uploadandoAnalise ? "Enviando..." : "Enviar"}
                          </Button>
                        )}
                        {arquivoAnalise && (
                          <Button
                            size="small"
                            onClick={() => {
                              setArquivoAnalise(null);
                              setNomeAnalise("");
                            }}
                            disabled={uploadandoAnalise}
                            sx={{
                              backgroundColor: "#d32f2f",
                              color: "#fff",
                              fontSize: "0.74rem",
                              "&:hover": { backgroundColor: "#c62828" },
                              "&.Mui-disabled": { backgroundColor: "var(--ig-f-333333)", color: "#555" },
                              textTransform: "none",
                              px: 1.5,
                              width: "auto",
                            }}
                          >
                            Cancelar
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  )}

                  {/* Observação da Análise de Crédito */}
                  <Paper
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      borderRadius: 2,
                      border: "1px solid var(--ig-b-333333)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "var(--ig-t-ffffff)",
                        fontWeight: 600,
                        fontSize: "0.87rem",
                        mb: 1,
                      }}
                    >
                      Observação
                    </Typography>

                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      value={observacaoAnaliseCredito}
                      onChange={(e) => {
                        if (propostaInfo.statusAnaliseCredito === "AguardandoUpload" && podeCadastrar) {
                          setObservacaoAnaliseCredito(e.target.value.toUpperCase());
                        }
                      }}
                      slotProps={{
                        input: {
                          readOnly: propostaInfo.statusAnaliseCredito !== "AguardandoUpload" || !podeCadastrar,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor:
                            propostaInfo.statusAnaliseCredito === "AguardandoUpload" && podeCadastrar ? "var(--ig-f-2a2a2a)" : "var(--ig-f-252525)",

                          color: "var(--ig-t-ffffff)",

                          "& fieldset": {
                            borderColor: "var(--ig-b-444444)",
                          },

                          "&:hover fieldset": {
                            borderColor:
                              propostaInfo.statusAnaliseCredito === "AguardandoUpload" && podeCadastrar ? "var(--ig-b-888888)" : "var(--ig-b-444444)",
                          },
                        },

                        "& textarea": {
                          color: "var(--ig-t-ffffff)",
                          textTransform: "uppercase",
                        },
                      }}
                    />
                  </Paper>

                  {/* Lista de documentos enviados */}
                  {docsAnalise.length > 0 && (
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, border: "1px solid var(--ig-b-333333)" }}>
                      <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.8rem", mb: 1 }}>Documentos enviados:</Typography>
                      {docsAnalise.map((doc) => (
                        <Box
                          key={doc.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            py: 0.75,
                            borderBottom: "1px solid var(--ig-b-2a2a2a)",
                          }}
                        >
                          <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.82rem" }}>
                            {doc.nomeOriginal}
                            <Typography component="span" sx={{ color: "var(--ig-t-666666)", fontSize: "0.75rem", ml: 1 }}>
                              {doc.dataUpload ? fmtData(doc.dataUpload) : ""}
                            </Typography>
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                              onClick={() => handleVisualizarAnaliseDoc(doc.id)}
                              sx={{
                                backgroundColor: "#00897b",
                                color: "#fff",
                                fontSize: "0.71rem",
                                minWidth: 0,
                                px: 1.2,
                                width: "auto",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#00695c" },
                              }}
                            >
                              Visualizar
                            </Button>
                            <Button
                              size="small"
                              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                              onClick={() => handleDownloadAnaliseDoc(doc.id, doc.nomeOriginal)}
                              sx={{
                                backgroundColor: "#1976d2",
                                color: "#fff",
                                fontSize: "0.71rem",
                                minWidth: 0,
                                px: 1.2,
                                width: "auto",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#1565c0" },
                              }}
                            >
                              Baixar
                            </Button>
                            {podeCadastrar && propostaInfo.statusAnaliseCredito === "AguardandoUpload" && (
                              <Button
                                size="small"
                                startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                onClick={() => handleDeleteAnaliseDoc(doc.id)}
                                sx={{
                                  backgroundColor: "#d32f2f",
                                  color: "#fff",
                                  fontSize: "0.71rem",
                                  minWidth: 0,
                                  px: 1.2,
                                  width: "auto",
                                  textTransform: "none",
                                  "&:hover": { backgroundColor: "#c62828" },
                                }}
                              >
                                Excluir
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Paper>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      onClick={handleVoltarParaLista}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                  </Box>
                </Box>
              )}

              {/* ── Aprovação da Análise de Crédito ──────────────────────────────── */}
              {aba === "aprovacaoAnalise" && propostaInfo?.statusAnaliseCredito && (
                <Box>
                  {(propostaInfo.statusAnaliseCredito === "EmAprovacao" ||
                    propostaInfo.statusAnaliseCredito === "Aprovada" ||
                    propostaInfo.statusAnaliseCredito === "Reprovada" ||
                    historicosAnalise.length > 0) && (
                    <PainelAprovacao
                      titulo="Aprovação da Análise de Crédito"
                      statusField={propostaInfo.statusAnaliseCredito}
                      statusNaoIniciado="AguardandoUpload"
                      mostrarCabecalho={false}
                      aprovacao={aprovacaoAnalise}
                      autorizacoes={autorizacoesAnalise}
                      historicos={historicosAnalise}
                      idUsuarioLogado={idUsuarioLogado}
                      idCriador={propostaInfo.idUsuarioCriacao ?? null}
                      revisarPorAlcada
                      usuarioEmAlcada={propostaInfo.usuarioEhAprovador}
                      usuarioEhAprovador={propostaInfo.usuarioEhAprovador}
                      onEnviar={handleEnviarAnaliseAprovacao}
                      enviando={enviandoAnalise}
                      onAprovar={handleAprovarAnalise}
                      onAbrirReprovar={() => setDlgReprovarAnalise({ aberto: true, motivo: "" })}
                      onAbrirQuestionar={() => setDlgQuestionarAnalise({ aberto: true, motivo: "", idsUsuarioResposta: [], arquivo: null })}
                      onAbrirResponder={(pergunta) => setDlgResponderAnalise({ aberto: true, resposta: "", pergunta, arquivo: null })}
                      onConfirmarRevisar={() => {
                        document.activeElement?.blur();
                        setDlgRevisarAnalise({ aberto: true, motivo: "" });
                      }}
                      mostrarBotaoEnviar={false}
                      processando={processandoAnalise}
                      podeEditar={podeCadastrar}
                      rotaAnexoBase="/api/propostas"
                    />
                  )}

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      onClick={handleVoltarParaLista}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* ── Diálogos de aprovação das FICHAS ──────────────────────────────── */}

        <DialogReprovarAprovacao
          aberto={dlgReprovarFichas.aberto}
          motivo={dlgReprovarFichas.motivo}
          onChangeMotivo={(motivo) => setDlgReprovarFichas((p) => ({ ...p, motivo }))}
          onCancelar={() => setDlgReprovarFichas({ aberto: false, motivo: "" })}
          onConfirmar={handleReprovarFichas}
          processando={processandoFichas}
          titulo="Reprovar Fichas de Cadastro"
        />

        <DialogQuestionarAprovacao
          aberto={dlgQuestionarFichas.aberto}
          motivo={dlgQuestionarFichas.motivo}
          onChangeMotivo={(motivo) => setDlgQuestionarFichas((p) => ({ ...p, motivo }))}
          idsUsuarioResposta={dlgQuestionarFichas.idsUsuarioResposta}
          onChangeUsuariosResposta={(idsUsuarioResposta) => setDlgQuestionarFichas((p) => ({ ...p, idsUsuarioResposta }))}
          arquivo={dlgQuestionarFichas.arquivo}
          onChangeArquivo={(arquivo) => setDlgQuestionarFichas((p) => ({ ...p, arquivo }))}
          usuariosAtivos={usuariosAprovadores}
          idUsuarioAtual={idUsuarioLogado}
          onCancelar={() => setDlgQuestionarFichas({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null })}
          onConfirmar={handleQuestionarFichas}
          processando={processandoFichas}
          titulo="Questionar Fichas"
          rows={5}
          maxLength={255}
        />

        <DialogResponderAprovacao
          aberto={dlgResponderFichas.aberto}
          resposta={dlgResponderFichas.resposta}
          pergunta={dlgResponderFichas.pergunta}
          onChangeResposta={(resposta) => setDlgResponderFichas((p) => ({ ...p, resposta }))}
          arquivo={dlgResponderFichas.arquivo}
          onChangeArquivo={(arquivo) => setDlgResponderFichas((p) => ({ ...p, arquivo }))}
          onCancelar={() => setDlgResponderFichas({ aberto: false, resposta: "", pergunta: "", arquivo: null })}
          onConfirmar={handleResponderFichas}
          processando={processandoFichas}
          titulo="Responder Questão — Fichas"
          rows={6}
          maxLength={255}
        />

        <DialogReprovarAprovacao
          aberto={dlgRevisarFichas.aberto}
          motivo={dlgRevisarFichas.motivo}
          onChangeMotivo={(motivo) => setDlgRevisarFichas((p) => ({ ...p, motivo }))}
          onCancelar={() => setDlgRevisarFichas({ aberto: false, motivo: "" })}
          onConfirmar={handleRevisarFichas}
          processando={processandoFichas}
          titulo="Revisar Fichas"
          contentText="As fichas serão devolvidas para preenchimento e o ciclo de aprovação atual será encerrado. Informe o motivo da revisão:"
          rows={4}
          maxLength={255}
          labelConfirmar="Confirmar"
          corConfirmar="#e65100"
        />

        {/* ── Diálogos de aprovação da ANÁLISE DE CRÉDITO ───────────────────── */}

        <DialogReprovarAprovacao
          aberto={dlgReprovarAnalise.aberto}
          motivo={dlgReprovarAnalise.motivo}
          onChangeMotivo={(motivo) => setDlgReprovarAnalise((p) => ({ ...p, motivo }))}
          onCancelar={() => setDlgReprovarAnalise({ aberto: false, motivo: "" })}
          onConfirmar={handleReprovarAnalise}
          processando={processandoAnalise}
          titulo="Reprovar Análise de Crédito"
        />

        <DialogQuestionarAprovacao
          aberto={dlgQuestionarAnalise.aberto}
          motivo={dlgQuestionarAnalise.motivo}
          onChangeMotivo={(motivo) => setDlgQuestionarAnalise((p) => ({ ...p, motivo }))}
          idsUsuarioResposta={dlgQuestionarAnalise.idsUsuarioResposta}
          onChangeUsuariosResposta={(idsUsuarioResposta) => setDlgQuestionarAnalise((p) => ({ ...p, idsUsuarioResposta }))}
          arquivo={dlgQuestionarAnalise.arquivo}
          onChangeArquivo={(arquivo) => setDlgQuestionarAnalise((p) => ({ ...p, arquivo }))}
          usuariosAtivos={usuariosAprovadores}
          idUsuarioAtual={idUsuarioLogado}
          onCancelar={() => setDlgQuestionarAnalise({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null })}
          onConfirmar={handleQuestionarAnalise}
          processando={processandoAnalise}
          titulo="Questionar Análise de Crédito"
        />

        <DialogResponderAprovacao
          aberto={dlgResponderAnalise.aberto}
          resposta={dlgResponderAnalise.resposta}
          pergunta={dlgResponderAnalise.pergunta}
          onChangeResposta={(resposta) => setDlgResponderAnalise((p) => ({ ...p, resposta }))}
          arquivo={dlgResponderAnalise.arquivo}
          onChangeArquivo={(arquivo) => setDlgResponderAnalise((p) => ({ ...p, arquivo }))}
          onCancelar={() => setDlgResponderAnalise({ aberto: false, resposta: "", pergunta: "", arquivo: null })}
          onConfirmar={handleResponderAnalise}
          processando={processandoAnalise}
          titulo="Responder Questão — Análise de Crédito"
        />

        <DialogReprovarAprovacao
          aberto={dlgRevisarAnalise.aberto}
          motivo={dlgRevisarAnalise.motivo}
          onChangeMotivo={(motivo) => setDlgRevisarAnalise((p) => ({ ...p, motivo }))}
          onCancelar={() => setDlgRevisarAnalise({ aberto: false, motivo: "" })}
          onConfirmar={handleRevisarAnalise}
          processando={processandoAnalise}
          titulo="Revisar Análise de Crédito"
          contentText="A análise de crédito será devolvida para novo upload e o ciclo de aprovação atual será encerrado. Informe o motivo da revisão:"
          rows={4}
          maxLength={255}
          labelConfirmar="Confirmar"
          corConfirmar="#e65100"
        />

        {/* Template de impressão das fichas — oculto na tela; conteúdo copiado para iframe ao imprimir */}
        <div
          style={{
            display: "none",
          }}
        >
          {fichasParaImprimir && (
            <FichaCadastroPrintTemplate
              proposta={fichasParaImprimir.proposta}
              fichas={fichasParaImprimir.fichas}
              modoTemplate={fichasParaImprimir.modoTemplate === true}
            />
          )}
        </div>

        <iframe
          ref={printFrameRef}
          title="print-frame-fichas"
          style={{
            display: "none",
            border: "none",
          }}
        />
        <Dialog
          open={dlgJustificativasDocs.aberto}
          onClose={() => {
            if (dlgJustificativasDocs.salvando) {
              return;
            }

            setDlgJustificativasDocs({
              aberto: false,

              grupos: [],

              valores: {},

              abaAtiva: 0,

              salvando: false,
            });
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "var(--ig-f-1e1e1e)",

              color: "var(--ig-t-ffffff)",

              border: "1px solid var(--ig-b-333333)",
            },
          }}
        >
          {/* ======================================================= */}
          {/* TÍTULO                                                  */}
          {/* ======================================================= */}

          <DialogTitle
            sx={{
              color: "var(--ig-t-ffffff)",

              fontWeight: 700,

              borderBottom: "1px solid var(--ig-b-333333)",
            }}
          >
            Justificativa dos Documentos Ausentes
          </DialogTitle>

          {/* ======================================================= */}
          {/* CONTEÚDO                                                */}
          {/* ======================================================= */}

          <DialogContent
            sx={{
              pt: "20px !important",
            }}
          >
            <Typography
              sx={{
                color: "var(--ig-t-bbbbbb)",

                mb: 2,

                fontSize: "0.9rem",
              }}
            >
              Os documentos abaixo não foram anexados. Informe uma justificativa individual para cada ausência antes de enviar as fichas para aprovação.
            </Typography>

            {/* ===================================================== */}
            {/* UMA ABA PARA CADA CHECKLIST                           */}
            {/* ===================================================== */}

            <Tabs
              value={dlgJustificativasDocs.abaAtiva}
              onChange={(_, novaAba) =>
                setDlgJustificativasDocs((prev) => ({
                  ...prev,

                  abaAtiva: novaAba,
                }))
              }
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mb: 2,

                borderBottom: "1px solid var(--ig-b-444444)",

                "& .MuiTab-root": {
                  color: "var(--ig-t-aaaaaa)",

                  fontWeight: 700,

                  textTransform: "none",

                  minHeight: 48,
                },

                "& .MuiTab-root.Mui-selected": {
                  color: "#1998a8",
                },

                "& .MuiTabs-indicator": {
                  backgroundColor: "#1998a8",
                },

                "& .MuiTabScrollButton-root": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            >
              {dlgJustificativasDocs.grupos.map((grupo) => (
                <Tab key={grupo.chave} label={`${grupo.titulo} - ${grupo.fichaNome}`} />
              ))}
            </Tabs>

            {/* ===================================================== */}
            {/* CONTEÚDO SOMENTE DA ABA ATIVA                        */}
            {/* ===================================================== */}

            {dlgJustificativasDocs.grupos.map((grupo, indiceGrupo) => {
              if (indiceGrupo !== dlgJustificativasDocs.abaAtiva) {
                return null;
              }

              return (
                <Box key={grupo.chave}>
                  {/* ================================================= */}
                  {/* IDENTIFICAÇÃO DA FICHA                            */}
                  {/* ================================================= */}

                  <Box
                    sx={{
                      p: 1.5,

                      mb: 2,

                      backgroundColor: "var(--ig-f-252525)",

                      border: "1px solid var(--ig-b-3a3a3a)",

                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#1998a8",

                        fontWeight: 700,

                        fontSize: "0.86rem",
                      }}
                    >
                      {grupo.fichaNome}
                    </Typography>

                    <Typography
                      sx={{
                        color: "var(--ig-t-aaaaaa)",

                        fontSize: "0.76rem",

                        mt: 0.4,
                      }}
                    >
                      Checklist: {grupo.titulo}
                    </Typography>
                  </Box>

                  {/* ================================================= */}
                  {/* DOCUMENTOS AUSENTES                               */}
                  {/* ================================================= */}

                  <Box
                    sx={{
                      display: "flex",

                      flexDirection: "column",

                      gap: 2,
                    }}
                  >
                    {grupo.itens.map((item) => (
                      <Box
                        key={item.chave}
                        sx={{
                          p: 1.5,

                          border: "1px solid var(--ig-b-3a3a3a)",

                          borderRadius: 1,

                          backgroundColor: "var(--ig-f-252525)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "var(--ig-t-ffffff)",

                            fontWeight: 600,

                            fontSize: "0.86rem",

                            mb: 1,
                          }}
                        >
                          {item.label}
                        </Typography>

                        <TextField
                          label="Justificativa da ausência*"
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={dlgJustificativasDocs.valores[item.chave] || ""}
                          onChange={(e) =>
                            setDlgJustificativasDocs((prev) => ({
                              ...prev,

                              valores: {
                                ...prev.valores,

                                [item.chave]: e.target.value.toUpperCase(),
                              },
                            }))
                          }
                          disabled={dlgJustificativasDocs.salvando}
                          sx={{
                            "& .MuiInputLabel-root": {
                              color: "var(--ig-t-aaaaaa)",

                              fontWeight: "bold",
                            },

                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "var(--ig-f-2a2a2a)",

                              color: "var(--ig-t-ffffff)",

                              "& fieldset": {
                                borderColor: "var(--ig-b-444444)",
                              },

                              "&:hover fieldset": {
                                borderColor: "var(--ig-b-888888)",
                              },
                            },

                            "& textarea": {
                              color: "var(--ig-t-ffffff)",

                              textTransform: "uppercase",
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </DialogContent>

          {/* ======================================================= */}
          {/* BOTÕES                                                  */}
          {/* ======================================================= */}

          <DialogActions
            sx={{
              px: 3,

              py: 2,

              borderTop: "1px solid var(--ig-b-333333)",
            }}
          >
            <Button
              variant="outlined"
              disabled={dlgJustificativasDocs.salvando}
              onClick={() =>
                setDlgJustificativasDocs({
                  aberto: false,

                  grupos: [],

                  valores: {},

                  abaAtiva: 0,

                  salvando: false,
                })
              }
              sx={{
                width: "auto",

                borderColor: "var(--ig-b-555555)",

                color: "var(--ig-t-cccccc)",

                fontWeight: "bold",

                "&:hover": {
                  borderColor: "var(--ig-b-777777)",

                  backgroundColor: "var(--ig-f-255-255-255-a005)",
                },
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              startIcon={
                dlgJustificativasDocs.salvando ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      color: "var(--ig-t-ffffff)",
                    }}
                  />
                ) : (
                  <SendIcon />
                )
              }
              disabled={dlgJustificativasDocs.salvando}
              onClick={confirmarJustificativasDocumentais}
              sx={{
                width: "auto",

                backgroundColor: "#1998a8",

                fontWeight: "bold",

                px: 3,

                "&:hover": {
                  backgroundColor: "#1565c0",
                },
              }}
            >
              {dlgJustificativasDocs.salvando ? "Salvando..." : "Confirmar e Enviar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snack.aberto}
          autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, aberto: false }))}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
